/**
 * Marketing API Service
 */
import { apiClient } from './api';

// HRMS Employee (from HRMS via marketing-api proxy)
export interface HRMSEmployee {
  id: number;
  user_id: number | null;
  username: string | null;
  first_name: string;
  last_name: string;
  email: string;
  employee_id: string | null;
  department: string | null;
  designation: string | null;
  is_active: boolean;
  /** Absolute URL, from HRMS's Employee.profile_picture. */
  profile_picture?: string | null;
}

// Local Marketing Employee (cached table)
export interface MarketingEmployee {
  id: number;
  hrms_employee_id: number | null;
  hrms_user_id: number | null;
  username: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  employee_code: string | null;
  department: string | null;
  designation: string | null;
  phone: string | null;
  is_active: boolean;
  role: string | null;
  domain_id: number | null;
  domain_name: string | null;
  region_id: number | null;
  region_name: string | null;
  synced_from_hrms: boolean;
  last_synced_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// Domain Types
export interface Domain {
  id: number;
  name: string;
  code: string;
  description?: string;
  is_active: boolean;
  is_export?: boolean;
  head_employee_id?: number;
  head_username?: string;
  head_email?: string;
  coordinator_employee_id?: number;
  coordinator_username?: string;
  coordinator_email?: string;
  created_by_employee_id?: number;
  created_by_username?: string;
  created_at: string;
  updated_at: string;
}

// Region Types
export interface Region {
  id: number;
  domain_id: number;
  name: string;
  code: string;
  description?: string;
  sort_order?: number;
  head_employee_id?: number;
  head_username?: string;
  coordinator_employee_id?: number;
  coordinator_username?: string;
  coordinator_email?: string;
  is_active: boolean;
  created_by_employee_id?: number;
  created_by_username?: string;
  created_at: string;
  updated_at: string;
  domain?: Domain;
}

/** Assignment with employee name/email from HRMS (from GET /api/regions/assignments/). */
export interface AssignmentWithEmployee {
  id: number;
  employee_id: number;
  region_id: number;
  role: string;
  is_active: boolean;
  region?: Region;
  employee_name?: string;
  employee_email?: string;
}

// Contact Types — company data from organization_id only (no company_name/website/industry on contact)
export interface Contact {
  id: number;
  title?: string;  // Salutation: Mr., Mrs., Dr., etc.
  first_name?: string;
  last_name?: string;
  contact_person_name?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_job_title?: string;
  domain_id?: number | null;
  region_id?: number | null;
  organization_id?: number | null;  // Optional – link contact to an organization
  plant_id?: number | null;         // Optional – link to organization's plant
  is_active: boolean;
  is_converted: boolean;
  converted_to_customer_id?: number;
  series_code?: string;  // Code of numbering series (character, not FK)
  series?: string;  // Generated number from numbering series (e.g. CONT-0001)
  notes?: string;
  source?: string;
  exhibition_id?: number | null;  // Exhibition/roadshow (Event) this contact was met at
  exhibition_name?: string | null;  // Populated by API for display
  created_by_employee_id: number;
  created_by_username?: string;
  assigned_to_employee_id?: number;
  assigned_to_username?: string;
  created_at: string;
  updated_at: string;
  domain?: Domain;
  region?: Region;
  organization?: Organization | null;  // Populated when API includes it
  plant?: Plant | null;  // Organization plant with address
}

// Visiting Card Contact Types — raw data pushed in from the external business-card system.
// Unscoped (no domain/region): anyone with view permission sees all records, like a shared inbox.
export interface VisitingCardContact {
  id: number;
  name?: string;
  company_name?: string;
  designation?: string;
  phone_numbers: string[];
  emails: string[];
  website?: string;
  social_media: string[];  // list of URLs
  address?: string;  // single free-text line
  notes?: string;
  source?: string;
  exhibition_id?: number | null;  // Exhibition/roadshow (Event id) the card was scanned at
  exhibition_name?: string | null;  // Populated by API for display
  is_converted: boolean;
  converted_to_contact_id?: number;
  created_by_employee_id?: number;
  created_by_username?: string;
  created_at: string;
  updated_at: string;
}

// Customer Types (primary contact via primary_contact_contact_id / primary_contact_contact)
export interface Customer {
  id: number;
  company_name: string;
  /** website, industry, company_size removed; use organization when organization_id set */
  tax_id?: string;
  domain_id: number;
  region_id?: number;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  is_active: boolean;
  customer_since?: string;
  converted_from_contact_id?: number;
  organization_id?: number | null;
  plant_id?: number | null;
  /** set by the customer search: name of this customer's plant */
  plant_name?: string | null;
  primary_contact_contact_id?: number | null;
  series_code?: string;
  series?: string;
  notes?: string;
  created_by_employee_id: number;
  created_by_username?: string;
  account_manager_employee_id?: number;
  account_manager_username?: string;
  created_at: string;
  updated_at: string;
  domain?: Domain;
  region?: Region;
  organization?: Organization | null;  // Populated when API includes it (for industry/website etc.)
  plants?: Plant[];
  primary_contact_contact?: Contact | null;  // Person data for display
}

// Plant Types
// Organization (has plants; customers link to organization + plant)
export interface Organization {
  id: number;
  name: string;
  code?: string;
  description?: string;
  website?: string;
  industry?: string;
  organization_size?: string;  // e.g. "1-10", "51-200"
  is_active: boolean;
  created_by_employee_id?: number;
  created_by_username?: string;
  created_at: string;
  updated_at: string;
}

export interface Plant {
  id: number;
  organization_id?: number;
  contact_id?: number;
  customer_id?: number;
  plant_name: string;
  plant_code?: string;
  domain_id?: number;
  region_id?: number;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  is_active: boolean;
  notes?: string;
  created_by_employee_id?: number;
  created_by_username?: string;
  created_at: string;
  updated_at: string;
}

/** Lead status group (e.g. Initialization, Follow, Quotation/Technical) with expected duration and optional follow-up interval */
export interface LeadStatusGroup {
  id: number;
  code: string;
  label: string;
  expected_duration_days?: number | null;  // e.g. 15, 90 (3 months)
  follow_up_interval_days?: number | null;  // create follow-up task every N days for leads in this group
  display_order: number;
  is_active: boolean;
  hex_color?: string | null;  // e.g. "#3b82f6" for badge/UI color
  created_at: string;
  updated_at: string;
}

// Lead status (from DB) – belongs to a LeadStatusGroup
export interface LeadStatusOption {
  id: number;
  code: string;
  label: string;
  group_id?: number | null;
  group?: LeadStatusGroup | null;  // Populated when API includes it
  stage?: string | null;  // Deprecated; use group_id / group
  display_order: number;
  is_active: boolean;
  is_final?: boolean;  // Won/closed – no follow-up reminders
  is_lost?: boolean;   // Lost – no follow-up reminders
  is_hot?: boolean;    // Mark leads in this status as hot cases
  hex_color?: string | null;  // e.g. "#3b82f6" for badge/UI color
  set_when_quotation_added?: boolean;  // Auto-set lead to this status when a quotation is added to any enquiry
  set_when_quote_number_generated?: boolean;  // Auto-set when quote number generated (Generate button) but no quotation file yet
  attachment_required_on_kanban_change?: boolean;  // Require attachment when changing to this status from kanban
  created_at: string;
  updated_at: string;
}

/** "Lead for" option (e.g. Standard Walk-in, Chamber, Project) */
export interface LeadTypeOption {
  id: number;
  code: string;
  label: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Lead through / source channel (e.g. Enq.Through, Regional Head, Website) */
export interface LeadThroughOption {
  id: number;
  code: string;
  label: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Lead Types (person/company from contact or customer; no duplicate fields)
export interface Lead {
  id: number;
  domain_id: number;
  region_id?: number;
  contact_id?: number;
  customer_id?: number;
  plant_id?: number;
  status_id?: number;
  status?: string;
  lead_type_id?: number;
  lead_through_id?: number;
  through_contact_id?: number | null;
  referred_by_customer_id?: number | null;
  potential_value?: number;
  quote_value?: number | null;
  quotation_count?: number;
  /** Base quotation numbers (lead list only) — lets the kanban search find a lead by QTN number. */
  quotation_numbers?: string[];
  notes?: string;
  exhibition_id?: number | null;  // Exhibition/roadshow this lead is attributed to (defaults from the linked contact)
  exhibition_name?: string | null;  // Populated by API for display
  closed_value?: number | null;
  closed_at?: string | null;
  series_code?: string;
  series?: string;
  quote_series_code?: string | null;
  quote_number?: string | null;
  next_follow_up_at?: string | null;
  /** Server-computed due date from the lead's status-group follow-up interval (e.g. "every 15 days"); null if the group has no interval or the lead is Won/Lost. Not stored — recomputed on every list fetch. */
  auto_follow_up_at?: string | null;
  follow_up_reminder_type?: string | null;
  assigned_to_employee_id?: number;
  assigned_to_username?: string;
  referred_by_employee_id?: number | null;
  created_by_employee_id: number;
  created_by_username?: string;
  created_at: string;
  updated_at: string;
  expected_closing_date?: string;
  domain?: Domain;
  region?: Region;
  contact?: Contact | null;   // Person/company for display
  customer?: Customer;
  through_contact?: Contact | null;  // Person who referred: contact
  referred_by_customer?: Customer | null;  // Person who referred: customer
  plant?: Plant;
  status_option?: LeadStatusOption;
  lead_type_option?: LeadTypeOption;
  lead_through_option?: LeadThroughOption;
  last_activity_date?: string | null;
  /** When lead is in Lost status: reason from "Marked as Lost" activity (e.g. in Orders Lost tab). */
  lost_reason?: string | null;
}

/** Activity attachment: either a quotation (trackable) or a general attachment */
export interface LeadActivityAttachment {
  id: number;
  activity_id: number;
  /** null when the quotation number was pre-generated with no file yet (attach later) */
  file_name?: string | null;
  file_path?: string | null;
  is_quotation?: boolean;
  quotation_number?: string | null;
  title?: string | null;
  quote_value?: number | null;
  file_size?: number;
  content_type?: string;
  media_exists?: boolean;
  created_at: string;
}

/** Activity log entry for lead history */
export interface LeadActivity {
  id: number;
  activity_type: string;
  title: string;
  description?: string;
  activity_date: string;
  lead_id?: number;
  contact_person_title?: string;  // Salutation: Mr., Mrs., Dr., etc.
  contact_person_name?: string;
  contact_person_email?: string;
  contact_person_phone?: string;
  from_status_id?: number;
  to_status_id?: number;
  from_status_name?: string;
  to_status_name?: string;
  inquiry_number?: number | null;
  attachments?: LeadActivityAttachment[];
  created_by_employee_id?: number;
  created_by_username?: string;
  created_by_name?: string;
  created_by_email?: string;
  created_at: string;
}

/** One of the current user's own lead logs for a day (GET /api/leads/activities/mine) —
 *  used by the HRMS DSR "Fill from lead activity" button. */
export interface MyLeadActivity {
  id: number;
  lead_id: number;
  activity_type: string;
  title: string;
  description?: string | null;
  activity_date: string;
  contact_person_name?: string | null;
  contact_person_email?: string | null;
  contact_person_phone?: string | null;
  lead_name: string;
  company_name?: string | null;
}

export interface Campaign {
  id: number;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  start_date?: string;
  end_date?: string;
  budget?: number;
  actual_cost?: number;
  created_by_employee_id?: number;
  created_by_username?: string;
  manager_employee_id?: number;
  manager_username?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateLeadRequest {
  contact_id?: number;  // Required: person/company from Contact or Customer
  customer_id?: number;
  plant_id?: number;
  domain_id: number;
  region_id?: number;
  status_id?: number;
  lead_type_id?: number;
  lead_through_id?: number;
  through_contact_id?: number | null;  // Person who referred: contact
  referred_by_customer_id?: number | null;  // Person who referred: customer
  potential_value?: number;
  notes?: string;
  exhibition_id?: number | null;  // Exhibition/roadshow to attribute this lead to; defaults from the linked contact
  series_code?: string;
  quote_series_code?: string;
  quote_number?: string;
  assigned_to_employee_id?: number;
  referred_by_employee_id?: number | null;  // Person who referred: employee
  created_by_employee_id?: number;
  expected_closing_date?: string;
  series?: string;
  /** ISO datetime — optional backdated first enquiry when creating a lead (omit when adding only an initial quotation with activity_date). */
  initial_inquiry_at?: string;
  /** Suppress the auto-created "Inquiry 0" quote-number placeholder — set when a quotation file is being uploaded in this same create flow. */
  skip_quote_placeholder?: boolean;
  /** Additional quotation numbers (beyond quote_number) with no file yet — become file-less quotation rows on the "Inquiry 0" activity when no files are uploaded in this create flow. For an entry generated from a series (as opposed to typed by hand), leave the matching extra_quote_numbers[i] empty and set extra_quote_series_codes[i] instead — the backend generates a real number for it at save time rather than trusting an earlier on-screen preview. */
  extra_quote_numbers?: string[];
  extra_quote_series_codes?: (string | null)[];
}

export interface UpdateLeadRequest extends Partial<CreateLeadRequest> {
  closed_value?: number;
  /** Backdate the Won date (ISO datetime). Requires marketing.create_lead; ignored/rejected otherwise. */
  closed_at?: string;
  series?: string;
  /** Required (min 100 chars) when moving lead to Lost status. Stored in enquiry log. */
  status_change_reason?: string;
  /** Competitor name (lost to whom); use "Not sure" if unknown. */
  lost_to_competitor?: string;
  /** Price at which lost; use "Not sure" if unknown. */
  lost_at_price?: string;
}

/** Lead display name from contact or customer (person/company data lives there). */
export function leadDisplayName(lead: Lead): string {
  if (lead.contact) {
    const c = lead.contact;
    const name = c.contact_person_name?.trim()
      || [c.first_name, c.last_name].filter(Boolean).join(' ').trim()
      || c.contact_email?.trim();
    if (name) return name;
  }
  if (lead.customer) return lead.customer.company_name || `Customer #${lead.customer_id}`;
  return `Lead #${lead.id}`;
}

/** Lead company name from contact's organization or customer. */
export function leadDisplayCompany(lead: Lead): string {
  if (lead.contact?.organization?.name) return lead.contact.organization.name;
  if (lead.customer?.company_name) return lead.customer.company_name;
  return '';
}

/** Contact company name from linked organization (for display). */
export function contactCompanyName(contact: Contact): string {
  return contact.organization?.name ?? '';
}

/** Lead email from contact or customer primary contact. */
export function leadDisplayEmail(lead: Lead): string {
  if (lead.contact?.contact_email) return lead.contact.contact_email;
  if (lead.customer?.primary_contact_contact?.contact_email) return lead.customer.primary_contact_contact.contact_email;
  return '';
}

/** Lead phone from contact or customer primary contact. */
export function leadDisplayPhone(lead: Lead): string {
  if (lead.contact?.contact_phone) return lead.contact.contact_phone;
  if (lead.customer?.primary_contact_contact?.contact_phone) return lead.customer.primary_contact_contact.contact_phone;
  return '';
}

/** Primary contact display name for Customer (from linked Contact). */
/** A short line that tells look-alike customers apart: "Customer #12 · Kalinganagar Plant · CUST-0012" */
export function customerDisambiguator(customer: Customer): string {
  const plant = customer.plant_name || customer.plants?.[0]?.plant_name || customer.city || '';
  return [`Customer #${customer.id}`, plant, customer.series].filter(Boolean).join(' · ');
}

export function customerPrimaryContactName(customer: Customer): string {
  const c = customer.primary_contact_contact;
  if (!c) return '';
  return c.contact_person_name?.trim()
    || [c.first_name, c.last_name].filter(Boolean).join(' ').trim()
    || c.contact_email?.trim()
    || '';
}

// Order types (orders from won leads; status groups, statuses, inquiry log)
export interface OrderStatusGroup {
  id: number;
  code: string;
  label: string;
  expected_duration_days?: number | null;
  display_order: number;
  is_active: boolean;
  hex_color?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrderStatusOption {
  id: number;
  code: string;
  label: string;
  group_id?: number | null;
  display_order: number;
  is_active: boolean;
  is_final: boolean;
  attachment_required_on_kanban_change?: boolean;
  hex_color?: string | null;
  group?: OrderStatusGroup | null;
  created_at: string;
  updated_at: string;
}

export interface Order {
  id: number;
  lead_id: number;
  status_id?: number | null;
  domain_id: number;
  region_id?: number | null;
  order_value?: number | null;
  expected_delivery_at?: string | null;
  notes?: string | null;
  series_code?: string | null;
  series?: string | null;
  assigned_to_employee_id?: number | null;
  assigned_to_username?: string | null;
  created_by_employee_id: number;
  created_by_username?: string | null;
  created_at: string;
  updated_at: string;
  domain?: Domain | null;
  region?: Region | null;
  status_option?: OrderStatusOption | null;
  status?: string | null;
  lead?: Lead | null;
  last_activity_date?: string | null;
}

export interface OrderActivityAttachment {
  id: number;
  order_activity_id: number;
  file_name: string;
  file_path: string;
  is_quotation?: boolean;
  quotation_number?: string | null;
  title?: string | null;
  file_size?: number | null;
  content_type?: string | null;
  created_at: string;
}

export interface OrderActivity {
  id: number;
  order_id: number;
  activity_type: string;
  title: string;
  description?: string | null;
  activity_date: string;
  contact_person_title?: string | null;
  contact_person_name?: string | null;
  contact_person_email?: string | null;
  contact_person_phone?: string | null;
  from_status_id?: number | null;
  to_status_id?: number | null;
  from_status_name?: string | null;
  to_status_name?: string | null;
  inquiry_number?: number | null;
  attachments?: OrderActivityAttachment[];
  created_by_employee_id?: number | null;
  created_by_username?: string | null;
  created_by_name?: string | null;
  created_by_email?: string | null;
  created_at: string;
}

export interface CreateOrderRequest {
  lead_id: number;
  status_id?: number | null;
  domain_id: number;
  region_id?: number | null;
  order_value?: number | null;
  expected_delivery_at?: string | null;
  notes?: string | null;
  series_code?: string | null;
  assigned_to_employee_id?: number | null;
}

export interface UpdateOrderRequest {
  status_id?: number | null;
  region_id?: number | null;
  order_value?: number | null;
  expected_delivery_at?: string | null;
  notes?: string | null;
  assigned_to_employee_id?: number | null;
  status_change_reason?: string;
  /** Set when the caller already saved its own status-change log entry (skips the automatic one). */
  skip_status_log?: boolean;
  /** Required (min 100 chars) when order_value changes. */
  value_change_reason?: string;
}

/** Paginated list response (page 1-based, page_size min 10). */
export interface AuditLog {
  id: number;
  employee_id: number;
  employee_name?: string;
  action: string;
  entity_type: string;
  entity_id: number | null;
  details: string | null;
  created_at: string;
  /** Lead name for "lead"/"quotation" entries (their details often say only "lead #N"). */
  entity_label?: string | null;
}

/** One result in the navbar global search (GET /api/search). */
export interface GlobalSearchHit {
  id: number;
  title: string;
  subtitle?: string | null;
  /** App route that opens the record. */
  href: string;
}

/** Global search results, grouped by type. A type is empty when the user can't view it. */
export interface GlobalSearchResponse {
  query: string;
  leads: GlobalSearchHit[];
  orders: GlobalSearchHit[];
  contacts: GlobalSearchHit[];
  customers: GlobalSearchHit[];
  organizations: GlobalSearchHit[];
}

/** Choices for the Audit Logs filter dropdowns (GET /api/audit-logs/filters). */
export interface AuditLogFilterOptions {
  entity_types: string[];
  actions: string[];
  users: { id: number; name: string }[];
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// ── Service module — Stage 1: Contracts ──────────────────────────────────────
export type ServiceContractType = 'AMC' | 'AMC-I' | 'CMC' | 'CMC-I';
export type ServiceContractStatus = 'draft' | 'active' | 'expired' | 'cancelled';
export type ServiceItemCoverage = 'included' | 'chargeable';

export const SERVICE_CONTRACT_TYPES: ServiceContractType[] = ['AMC', 'AMC-I', 'CMC', 'CMC-I'];
export const SERVICE_CONTRACT_STATUSES: ServiceContractStatus[] = ['draft', 'active', 'expired', 'cancelled'];

export interface ServiceContractItem {
  id?: number;
  contract_id?: number;
  name: string;
  coverage: ServiceItemCoverage;
  note?: string | null;
  display_order?: number;
  created_at?: string;
}

export interface ServiceContractPartAlias {
  id?: number;
  contract_id?: number;
  our_name: string;
  customer_name: string;
  display_order?: number;
}

export interface ServiceContract {
  id: number;
  series_code?: string | null;
  contract_number?: string | null;
  customer_id: number;
  plant_id?: number | null;
  contract_type: ServiceContractType;
  status: ServiceContractStatus;
  start_date?: string | null;
  end_date?: string | null;
  terms?: string | null;
  all_inclusive_with_charges: boolean;
  additional_charges_note?: string | null;
  notes?: string | null;
  items: ServiceContractItem[];
  part_aliases?: ServiceContractPartAlias[];
  customer_name?: string | null;
  customer_contact_name?: string | null;
  plant_name?: string | null;
  created_by_employee_id: number;
  created_by_username?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ServiceContractPayload {
  customer_id: number;
  plant_id?: number | null;
  contract_type: ServiceContractType;
  status?: ServiceContractStatus;
  start_date?: string | null;
  end_date?: string | null;
  terms?: string | null;
  all_inclusive_with_charges?: boolean;
  additional_charges_note?: string | null;
  notes?: string | null;
  series_code?: string | null;
  items?: ServiceContractItem[];
  part_aliases?: ServiceContractPartAlias[];
}

// ── Service module — Stage 2: Plans & Visits ─────────────────────────────────
export type ServiceVisitKind = 'scheduled' | 'unscheduled';
export type ServiceVisitStatus = 'planned' | 'scheduled' | 'done' | 'cancelled';

export const SERVICE_VISIT_STATUSES: ServiceVisitStatus[] = ['planned', 'scheduled', 'done', 'cancelled'];

export interface ServiceVisitReschedule {
  id: number;
  visit_id: number;
  from_date?: string | null;
  to_date?: string | null;
  reason: string;
  created_by_username?: string | null;
  created_at: string;
}

export interface ServiceVisit {
  id: number;
  plan_id?: number | null;
  contract_id?: number | null;
  complaint_id?: number | null;
  complaint_number?: string | null;
  customer_id?: number | null;
  plant_id?: number | null;
  kind: ServiceVisitKind;
  visit_number?: number | null;
  title?: string | null;
  status: ServiceVisitStatus;
  planned_date?: string | null;
  scheduled_date?: string | null;
  previous_scheduled_date?: string | null;
  completed_at?: string | null;
  assigned_engineer_employee_id?: number | null;
  assigned_engineer_username?: string | null;
  notes?: string | null;
  visit_type: 'normal' | 'fitting_only' | 'migration';
  fitting_part?: string | null;
  migration_machine_make?: string | null;
  migration_material_sent?: boolean | null;
  migration_departments?: string | null;
  engineer_notes?: string | null;
  created_by_employee_id: number;
  created_by_username?: string | null;
  created_at: string;
  updated_at: string;
  reschedules: ServiceVisitReschedule[];
  customer_name?: string | null;
  plant_name?: string | null;
  contract_number?: string | null;
  has_report?: boolean;
  /** this visit's own single-visit work order (older / complaint visits); contract work orders are not per visit */
  work_order_id?: number | null;
  report_status?: 'draft' | 'submitted' | null;
}

// ── Service module — Stage 5: visit execution, calibration, PO variance, reports ──
export type ServiceCalibrationKind = 'wl' | 'wol';
export type POVarianceResponseValue = 'pending' | 'accepted' | 'rejected';

export interface VisitAttachment {
  id: number;
  visit_id: number;
  file_name: string;
  file_size?: number | null;
  content_type?: string | null;
  caption?: string | null;
  created_by_username?: string | null;
  created_at: string;
}

export interface ServiceCalibration {
  id?: number;
  visit_id?: number;
  kind: ServiceCalibrationKind;
  duration_hours?: number | null;
  compressor_count?: number | null;
  notes?: string | null;
  created_at?: string;
}

export interface ServicePOVariance {
  id: number;
  visit_id: number;
  po_requirement: string;
  actual_requirement: string;
  additional_charge?: number | null;
  customer_response: POVarianceResponseValue;
  email_sent_at?: string | null;
  responded_at?: string | null;
  response_note?: string | null;
  charge_applies: boolean;
  created_by_username?: string | null;
  created_at: string;
}

export interface ServiceReport {
  id: number;
  visit_id: number;
  summary?: string | null;
  imported_data?: string | null;
  status: 'draft' | 'submitted';
  submitted_by_username?: string | null;
  submitted_at?: string | null;
  sent_to_accounts_at?: string | null;
  feedback_email_sent_at?: string | null;
  created_by_username?: string | null;
  created_at: string;
  updated_at: string;
}

export interface VisitReportBundle {
  visit: ServiceVisit;
  calibrations: ServiceCalibration[];
  po_variances: ServicePOVariance[];
  attachments: VisitAttachment[];
  report: ServiceReport | null;
}

export interface ServicePlan {
  id: number;
  contract_id: number;
  scheduled_visits_planned: number;
  preferred_schedule_note?: string | null;
  notes?: string | null;
  created_by_employee_id: number;
  created_by_username?: string | null;
  created_at: string;
  updated_at: string;
  visits: ServiceVisit[];
  contract_number?: string | null;
  customer_name?: string | null;
  contract_type?: string | null;
}

// ── Service module — Stage 3: Work Orders, Material Dispatch, Prerequisites ───
export type ServiceWorkOrderStatus = 'prepared' | 'checked' | 'approved';
export type ServiceDispatchStatus = 'not_dispatched' | 'partial' | 'full';

export const SERVICE_DISPATCH_STATUSES: ServiceDispatchStatus[] = ['not_dispatched', 'partial', 'full'];

/** The client's work order document (PDF / Excel / Word) for a visit */
export interface WorkOrderFile {
  id: number;
  work_order_id: number;
  visit_id?: number | null;
  file_name: string;
  file_size?: number | null;
  content_type?: string | null;
  uploaded_by_username?: string | null;
  created_at: string;
}

/** One shipment of a part — a part can be sent several times, each recorded on its own */
export interface WorkOrderDispatch {
  id: number;
  material_id: number;
  sent_on: string;
  quantity_sent?: string | null;
  docket_no?: string | null;
  transporter?: string | null;
  note?: string | null;
  created_by_username?: string | null;
  created_at: string;
}

export interface WorkOrderDispatchInput {
  sent_on?: string | null;
  quantity_sent?: string | null;
  docket_no?: string | null;
  transporter?: string | null;
  note?: string | null;
  completes_part?: boolean;
}

export interface WorkOrderMaterialAttachment {
  id: number;
  material_id: number;
  file_name: string;
  file_size?: number | null;
  content_type?: string | null;
  created_at: string;
}

export interface WorkOrderMaterial {
  id?: number;
  work_order_id?: number;
  item_name: string;
  /** which visit the part is for (contract-level work orders); null = whole year */
  visit_id?: number | null;
  visit_title?: string | null;
  visit_date?: string | null;
  customer_item_name?: string | null;
  quantity?: string | null;
  unit?: string | null;
  unit_note?: string | null;
  description?: string | null;
  required_by_date?: string | null;
  display_order?: number;
  dispatch_status?: ServiceDispatchStatus;
  dispatched_note?: string | null;
  dispatched_at?: string | null;
  dispatched_by_username?: string | null;
  created_at?: string;
  attachments?: WorkOrderMaterialAttachment[];
  dispatches?: WorkOrderDispatch[];
}

export interface WorkOrderPrerequisite {
  id?: number;
  work_order_id?: number;
  text: string;
  display_order?: number;
  is_done?: boolean;
  confirmed_by_username?: string | null;
  confirmed_at?: string | null;
  created_at?: string;
}

/** One level of a work order's HRMS approval chain */
export interface WorkOrderApprovalStep {
  level: number;
  approver_username?: string | null;
  approver_name?: string | null;
  approver_employee_id?: number | null;
  approver_user_id?: number | null;
}

/** History entry: a level approved, or the work order sent back */
export interface WorkOrderApprovalRecord {
  id: number;
  level?: number | null;
  action: 'approved' | 'sent_back' | string;
  by_username?: string | null;
  note?: string | null;
  created_at: string;
}

export interface ServiceWorkOrder {
  id: number;
  /** null = the contract's single work order covering all its visits */
  visit_id?: number | null;
  contract_id?: number | null;
  customer_id?: number | null;
  plant_id?: number | null;
  series_code?: string | null;
  wo_number?: string | null;
  /** departments the work order is sent to (one or several) */
  departments?: { id: number; name: string; head_name?: string | null }[];
  files?: WorkOrderFile[];
  status: ServiceWorkOrderStatus;
  revision: number;
  /** HRMS approval template (category service_work_order). Empty chain = plain permission buttons. */
  approval_template_name?: string | null;
  approval_chain?: WorkOrderApprovalStep[];
  current_level?: number;
  rejection_reason?: string | null;
  /** how many times it was sent back / reopened */
  times_reopened?: number;
  approvals?: WorkOrderApprovalRecord[];
  prepared_by_username?: string | null;
  prepared_at?: string | null;
  checked_by_username?: string | null;
  checked_at?: string | null;
  approved_by_username?: string | null;
  approved_at?: string | null;
  material_lead_time_days: number;
  prerequisites_sent_at?: string | null;
  notes?: string | null;
  created_by_employee_id: number;
  created_by_username?: string | null;
  created_at: string;
  updated_at: string;
  materials: WorkOrderMaterial[];
  prerequisites: WorkOrderPrerequisite[];
  customer_name?: string | null;
  customer_contact_name?: string | null;
  customer_contact_phone?: string | null;
  customer_contact_email?: string | null;
  plant_name?: string | null;
  plant_address?: string | null;
  contract_number?: string | null;
  visit_title?: string | null;
  visit_date?: string | null;
  dispatch_summary?: string | null;
}

export interface ServiceWorkOrderPayload {
  /** give exactly one: contract_id (one work order for all the contract's visits) or visit_id (a complaint visit) */
  contract_id?: number;
  visit_id?: number;
  departments?: { id: number; name: string }[];
  material_lead_time_days?: number;
  notes?: string | null;
  series_code?: string | null;
  materials?: WorkOrderMaterial[];
  prerequisites?: WorkOrderPrerequisite[];
}

/** Where to open a work order: a contract's single work order, or a single-visit one (complaint / older visits). */
export const workOrderPath = (w: { visit_id?: number | null; contract_id?: number | null }): string =>
  w.visit_id ? `/service/visits/${w.visit_id}/work-order` : `/service/contracts/${w.contract_id}/work-order`;

/** A visit's work order: plan visits share the contract's work order (unless they still have an older single-visit one); complaint visits have their own. */
export const visitWorkOrderPath = (v: { id: number; plan_id?: number | null; contract_id?: number | null; work_order_id?: number | null }): string =>
  v.plan_id && v.contract_id && !v.work_order_id ? `/service/contracts/${v.contract_id}/work-order` : `/service/visits/${v.id}/work-order`;

// ── Service module — Stage 4: Complaints ─────────────────────────────────────
/** An issue type code: 'hw' | 'sw' | 'plc' are built in; more can be added, so this is any string */
export type ServiceIssueType = string;

export interface ServiceReportSummary {
  group_by: 'company' | 'equipment' | 'person' | 'department';
  columns: { key: string; label: string }[];
  rows: { label: string; values: Record<string, number> }[];
  totals: Record<string, number>;
}

export interface ServiceIssueTypeOption {
  id: number;
  code: string;
  label: string;
  is_active: boolean;
  display_order: number;
}
export type ServiceComplaintStatus = 'pending_approval' | 'open' | 'in_progress' | 'resolved' | 'closed';
export type ServiceComplaintSource = 'customer' | 'found_on_visit';

export const SERVICE_ISSUE_TYPES: { value: ServiceIssueType; label: string }[] = [
  { value: 'hw', label: 'Hardware (H/W)' },
  { value: 'sw', label: 'Software (S/W)' },
  { value: 'plc', label: 'PLC' },
];

export interface ServiceComplaintActivity {
  id: number;
  complaint_id: number;
  activity_type: string;
  from_value?: string | null;
  to_value?: string | null;
  note?: string | null;
  created_by_username?: string | null;
  created_at: string;
}

export interface ServiceComplaint {
  id: number;
  series_code?: string | null;
  complaint_number?: string | null;
  display_number: string;
  customer_id: number;
  plant_id?: number | null;
  contract_id?: number | null;
  contract_number?: string | null;
  contract_type?: string | null;
  visit_id?: number | null;
  issue_type: ServiceIssueType;
  title: string;
  description?: string | null;
  status: ServiceComplaintStatus;
  source: ServiceComplaintSource;
  assignee_employee_id?: number | null;
  assignee_username?: string | null;
  assignee_department_id?: number | null;
  assignee_department_name?: string | null;
  approved_by_username?: string | null;
  approved_at?: string | null;
  reopen_count: number;
  planned_time_hours?: number | null;
  actual_time_hours?: number | null;
  resolved_at?: string | null;
  closed_at?: string | null;
  service_report_id?: number | null;
  created_by_employee_id: number;
  created_by_username?: string | null;
  created_at: string;
  updated_at: string;
  activities: ServiceComplaintActivity[];
  customer_name?: string | null;
  plant_name?: string | null;
}

export interface ServiceComplaintPayload {
  customer_id: number;
  plant_id?: number | null;
  contract_id?: number | null;
  issue_type: ServiceIssueType;
  title: string;
  description?: string | null;
  source?: ServiceComplaintSource;
  visit_id?: number | null;
  planned_time_hours?: number | null;
  series_code?: string | null;
  assignee_employee_id?: number | null;
  assignee_username?: string | null;
}

class MarketingAPIService {
  // Leads
  async getLeads(params?: {
    page?: number;
    page_size?: number;
    no_limit?: boolean;
    status_id?: number;
    /** Filter by assigned-to employee ID(s); multiple allowed */
    assigned_to?: number[];
    /** Only leads created by the current user (generated by me) */
    created_by_me?: boolean;
    /** Include leads in Won or Lost status; when false (default), backend excludes them */
    include_won_lost?: boolean;
    /** Only return leads in Lost status (for Orders Lost tab) */
    lost_only?: boolean;
    /** Only return leads with hot status */
    is_hot?: boolean;
    domain_id?: number;
    region_id?: number;
    search?: string;
    date_from?: string;
    date_to?: string;
    order_by?: string;
    order_dir?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Lead>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params?.page ?? 1));
    queryParams.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.no_limit === true) queryParams.append('no_limit', 'true');
    if (params?.status_id != null) queryParams.append('status_id', params.status_id.toString());
    if (params?.assigned_to?.length) params.assigned_to.forEach((id) => queryParams.append('assigned_to', String(id)));
    if (params?.created_by_me === true) queryParams.append('created_by_me', 'true');
    if (params?.include_won_lost === true) queryParams.append('include_won_lost', 'true');
    if (params?.lost_only === true) queryParams.append('lost_only', 'true');
    if (params?.is_hot === true) queryParams.append('is_hot', 'true');
    if (params?.domain_id != null) queryParams.append('domain_id', params.domain_id.toString());
    if (params?.region_id != null) queryParams.append('region_id', params.region_id.toString());
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.order_by) queryParams.append('order_by', params.order_by);
    if (params?.order_dir) queryParams.append('order_dir', params.order_dir);
    return apiClient.get<PaginatedResponse<Lead>>(`/api/leads/?${queryParams.toString()}`);
  }

  async getLead(id: number): Promise<Lead> {
    return apiClient.get<Lead>(`/api/leads/${id}`);
  }

  /** Lead statuses from DB (for filters, kanban, forms, and CRUD) */
  async getLeadStatuses(params?: { is_active?: boolean }): Promise<LeadStatusOption[]> {
    const q = new URLSearchParams();
    if (params?.is_active !== undefined) q.append('is_active', String(params.is_active));
    const query = q.toString();
    return apiClient.get<LeadStatusOption[]>(query ? `/api/leads/statuses/?${query}` : '/api/leads/statuses/');
  }

  async createLeadStatus(data: { code: string; label: string; group_id?: number; stage?: string; display_order?: number; is_active?: boolean; is_final?: boolean; is_lost?: boolean; hex_color?: string; set_when_quotation_added?: boolean; set_when_quote_number_generated?: boolean; attachment_required_on_kanban_change?: boolean; is_hot?: boolean }): Promise<LeadStatusOption> {
    return apiClient.post<LeadStatusOption>('/api/leads/statuses/', data);
  }

  async updateLeadStatus(id: number, data: Partial<{ code: string; label: string; group_id?: number; stage?: string; display_order: number; is_active: boolean; is_final: boolean; is_lost: boolean; hex_color?: string; set_when_quotation_added: boolean; set_when_quote_number_generated: boolean; attachment_required_on_kanban_change: boolean; is_hot: boolean }>): Promise<LeadStatusOption> {
    return apiClient.put<LeadStatusOption>(`/api/leads/statuses/${id}`, data);
  }

  async deleteLeadStatus(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/leads/statuses/${id}`);
  }

  /** Lead status groups (each group has expected duration in days; statuses belong to a group) */
  async getLeadStatusGroups(params?: { is_active?: boolean }): Promise<LeadStatusGroup[]> {
    const q = new URLSearchParams();
    if (params?.is_active !== undefined) q.append('is_active', String(params.is_active));
    const query = q.toString();
    return apiClient.get<LeadStatusGroup[]>(query ? `/api/leads/status-groups/?${query}` : '/api/leads/status-groups/');
  }

  async createLeadStatusGroup(data: { code: string; label: string; expected_duration_days?: number; follow_up_interval_days?: number; display_order?: number; is_active?: boolean; hex_color?: string }): Promise<LeadStatusGroup> {
    return apiClient.post<LeadStatusGroup>('/api/leads/status-groups/', data);
  }

  async updateLeadStatusGroup(id: number, data: Partial<{ code: string; label: string; expected_duration_days: number; follow_up_interval_days: number; display_order: number; is_active: boolean; hex_color?: string }>): Promise<LeadStatusGroup> {
    return apiClient.put<LeadStatusGroup>(`/api/leads/status-groups/${id}`, data);
  }

  async deleteLeadStatusGroup(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/leads/status-groups/${id}`);
  }

  /** Lead types ("Lead for": Standard Walk-in, Chamber, Project, etc.) */
  async getLeadTypes(params?: { is_active?: boolean }): Promise<LeadTypeOption[]> {
    const q = new URLSearchParams();
    if (params?.is_active !== undefined) q.append('is_active', String(params.is_active));
    const query = q.toString();
    return apiClient.get<LeadTypeOption[]>(query ? `/api/leads/types/?${query}` : '/api/leads/types/');
  }

  async createLeadType(data: { code: string; label: string; display_order?: number; is_active?: boolean }): Promise<LeadTypeOption> {
    return apiClient.post<LeadTypeOption>('/api/leads/types/', data);
  }

  async updateLeadType(id: number, data: Partial<{ code: string; label: string; display_order: number; is_active: boolean }>): Promise<LeadTypeOption> {
    return apiClient.put<LeadTypeOption>(`/api/leads/types/${id}`, data);
  }

  async deleteLeadType(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/leads/types/${id}`);
  }

  /** Lead through options (source/channel: Enq.Through, Regional Head, Website, etc.) */
  async getLeadThroughOptions(params?: { is_active?: boolean }): Promise<LeadThroughOption[]> {
    const q = new URLSearchParams();
    if (params?.is_active !== undefined) q.append('is_active', String(params.is_active));
    const query = q.toString();
    return apiClient.get<LeadThroughOption[]>(query ? `/api/leads/through/?${query}` : '/api/leads/through/');
  }

  /** Lead activity / history logs */
  async getLeadActivities(leadId: number): Promise<LeadActivity[]> {
    return apiClient.get<LeadActivity[]>(`/api/leads/${leadId}/activities/`);
  }

  /** The current user's own work lead logs (calls, emails, meetings, quotations…) in [start, end), oldest first. */
  async getMyLeadActivities(start: string, end: string): Promise<MyLeadActivity[]> {
    const qs = new URLSearchParams({ start, end });
    return apiClient.get<MyLeadActivity[]>(`/api/leads/activities/mine?${qs}`);
  }

  async createLeadActivity(
    leadId: number,
    data: {
      activity_type: string;
      title: string;
      description?: string;
      activity_date?: string;
      from_status_id?: number;
      to_status_id?: number;
      contact_person_title?: string;
      contact_person_name?: string;
      contact_person_email?: string;
      contact_person_phone?: string;
    }
  ): Promise<LeadActivity> {
    return apiClient.post<LeadActivity>(`/api/leads/${leadId}/activities/`, data);
  }

  /** Upload files as quotations or attachments. If seriesCode is set, first quotation gets number from series (with lead context), rest get base(rev2), base(rev3). */
  async uploadLeadActivityAttachments(
    leadId: number,
    activityId: number,
    files: File[],
    attachmentTypes: ('quotation' | 'attachment')[],
    quotationNumbers?: (string | undefined)[],
    titles?: (string | undefined)[],
    seriesCode?: string,
    isRevised?: boolean,
    quoteValues?: (number | undefined)[],
    onProgress?: (percent: number) => void,
  ): Promise<LeadActivityAttachment[]> {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    formData.append('attachment_types', JSON.stringify(attachmentTypes));
    if (quotationNumbers && quotationNumbers.length > 0) {
      formData.append('quotation_numbers', JSON.stringify(quotationNumbers));
    }
    if (titles && titles.length > 0) {
      formData.append('titles', JSON.stringify(titles));
    }
    if (seriesCode && seriesCode.trim()) {
      formData.append('series_code', seriesCode.trim());
    }
    if (isRevised) {
      formData.append('is_revised', 'true');
    }
    if (quoteValues && quoteValues.length > 0) {
      formData.append('quote_values', JSON.stringify(quoteValues));
    }
    return apiClient.postFormDataWithProgress<LeadActivityAttachment[]>(
      `/api/leads/${leadId}/activities/${activityId}/attachments`,
      formData,
      onProgress
    );
  }

  /** Create file-less quotation rows (pre-generated quotation numbers with no file yet) on an activity. When seriesCode is given (and no numbers), the next value is generated and committed server-side at save time. */
  async createLeadQuotationPlaceholders(
    leadId: number,
    activityId: number,
    quotationNumbers: string[],
    quoteValues?: (number | null)[],
    seriesCode?: string,
  ): Promise<LeadActivityAttachment[]> {
    return apiClient.post<LeadActivityAttachment[]>(
      `/api/leads/${leadId}/activities/${activityId}/quotations`,
      {
        quotation_numbers: quotationNumbers,
        quote_values: quoteValues,
        series_code: seriesCode,
      }
    );
  }

  /** Get an activity attachment blob URL for preview. */
  async getLeadActivityAttachmentUrl(
    leadId: number,
    activityId: number,
    attachmentId: number
  ): Promise<string> {
    const blob = await apiClient.getBlob(
      `/api/leads/${leadId}/activities/${activityId}/attachments/${attachmentId}/download`
    );
    return URL.createObjectURL(blob);
  }

  /** Download an activity attachment file (uses auth; triggers browser save). */
  async downloadLeadActivityAttachment(
    leadId: number,
    activityId: number,
    attachmentId: number,
    fileName: string
  ): Promise<void> {
    const blob = await apiClient.getBlob(
      `/api/leads/${leadId}/activities/${activityId}/attachments/${attachmentId}/download`
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'attachment';
    a.click();
    URL.revokeObjectURL(url);
  }

  async deleteLeadActivityAttachment(leadId: number, activityId: number, attachmentId: number): Promise<void> {
    return apiClient.delete<void>(`/api/leads/${leadId}/activities/${activityId}/attachments/${attachmentId}`);
  }

  /** Replace the file for an existing attachment (reattach). Preserves metadata. */
  async reattachLeadActivityAttachment(
    leadId: number,
    activityId: number,
    attachmentId: number,
    file: File
  ): Promise<LeadActivityAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.postFormData<LeadActivityAttachment>(
      `/api/leads/${leadId}/activities/${activityId}/attachments/${attachmentId}/replace`,
      formData
    );
  }

  /** Update the monetary value of an attachment (e.g. a file-less quotation placeholder) so it counts toward targets. */
  async updateLeadActivityAttachmentQuoteValue(
    leadId: number,
    activityId: number,
    attachmentId: number,
    quoteValue: number | null
  ): Promise<LeadActivityAttachment> {
    return apiClient.put<LeadActivityAttachment>(
      `/api/leads/${leadId}/activities/${activityId}/attachments/${attachmentId}`,
      { quote_value: quoteValue }
    );
  }

  async updateLeadActivity(
    leadId: number,
    activityId: number,
    data: {
      activity_type?: string;
      title?: string;
      description?: string;
      activity_date?: string;
      contact_person_title?: string;
      contact_person_name?: string;
      contact_person_email?: string;
      contact_person_phone?: string;
      from_status_id?: number;
      to_status_id?: number;
    }
  ): Promise<LeadActivity> {
    return apiClient.put<LeadActivity>(`/api/leads/${leadId}/activities/${activityId}`, data);
  }

  async deleteLeadActivity(leadId: number, activityId: number): Promise<void> {
    return apiClient.delete<void>(`/api/leads/${leadId}/activities/${activityId}`);
  }

  async createLead(data: CreateLeadRequest): Promise<Lead> {
    return apiClient.post<Lead>('/api/leads/', data);
  }

  async updateLead(id: number, data: UpdateLeadRequest): Promise<Lead> {
    return apiClient.put<Lead>(`/api/leads/${id}`, data);
  }

  /** Update lead number series and assign next value (requires marketing.admin). */
  async updateLeadSeries(leadId: number, data: { series_code: string }): Promise<Lead> {
    return apiClient.patch<Lead>(`/api/leads/${leadId}/series`, data);
  }

  async deleteLead(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/leads/${id}`);
  }

  // Orders (from won leads; status groups, statuses, inquiry log like leads)
  async getOrders(params?: {
    page?: number;
    page_size?: number;
    status_id?: number;
    assigned_to?: number | number[];
    lead_id?: number;
    date_from?: string;
    date_to?: string;
    created_by_me?: boolean;
    no_limit?: boolean;
  }): Promise<PaginatedResponse<Order>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params?.page ?? 1));
    queryParams.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.status_id != null) queryParams.append('status_id', params.status_id.toString());
    if (params?.assigned_to != null) {
      (Array.isArray(params.assigned_to) ? params.assigned_to : [params.assigned_to]).forEach((id) => queryParams.append('assigned_to', String(id)));
    }
    if (params?.lead_id != null) queryParams.append('lead_id', params.lead_id.toString());
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.created_by_me) queryParams.append('created_by_me', 'true');
    if (params?.no_limit) queryParams.append('no_limit', 'true');
    return apiClient.get<PaginatedResponse<Order>>(`/api/orders/?${queryParams.toString()}`);
  }

  async getOrder(id: number): Promise<Order> {
    return apiClient.get<Order>(`/api/orders/${id}`);
  }

  async getOrderStatusGroups(params?: { is_active?: boolean }): Promise<OrderStatusGroup[]> {
    const q = new URLSearchParams();
    if (params?.is_active !== undefined) q.append('is_active', String(params.is_active));
    const query = q.toString();
    return apiClient.get<OrderStatusGroup[]>(query ? `/api/orders/status-groups/?${query}` : '/api/orders/status-groups/');
  }

  async createOrderStatusGroup(data: { code: string; label: string; expected_duration_days?: number; display_order?: number; is_active?: boolean; hex_color?: string }): Promise<OrderStatusGroup> {
    return apiClient.post<OrderStatusGroup>('/api/orders/status-groups/', data);
  }

  async updateOrderStatusGroup(id: number, data: Partial<{ code: string; label: string; expected_duration_days?: number; display_order?: number; is_active?: boolean; hex_color?: string }>): Promise<OrderStatusGroup> {
    return apiClient.put<OrderStatusGroup>(`/api/orders/status-groups/${id}`, data);
  }

  async deleteOrderStatusGroup(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/orders/status-groups/${id}`);
  }

  async getOrderStatuses(params?: { is_active?: boolean }): Promise<OrderStatusOption[]> {
    const q = new URLSearchParams();
    if (params?.is_active !== undefined) q.append('is_active', String(params.is_active));
    const query = q.toString();
    return apiClient.get<OrderStatusOption[]>(query ? `/api/orders/statuses/?${query}` : '/api/orders/statuses/');
  }

  async createOrderStatus(data: { code: string; label: string; group_id?: number; display_order?: number; is_active?: boolean; is_final?: boolean; hex_color?: string; attachment_required_on_kanban_change?: boolean }): Promise<OrderStatusOption> {
    return apiClient.post<OrderStatusOption>('/api/orders/statuses/', data);
  }

  async updateOrderStatus(id: number, data: Partial<{ code: string; label: string; group_id?: number; display_order?: number; is_active?: boolean; is_final?: boolean; hex_color?: string; attachment_required_on_kanban_change: boolean }>): Promise<OrderStatusOption> {
    return apiClient.put<OrderStatusOption>(`/api/orders/statuses/${id}`, data);
  }

  async deleteOrderStatus(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/orders/statuses/${id}`);
  }

  async createOrder(data: CreateOrderRequest): Promise<Order> {
    return apiClient.post<Order>('/api/orders/', data);
  }

  async updateOrder(id: number, data: UpdateOrderRequest): Promise<Order> {
    return apiClient.put<Order>(`/api/orders/${id}`, data);
  }

  async deleteOrder(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/orders/${id}`);
  }

  async getOrderActivities(orderId: number): Promise<OrderActivity[]> {
    return apiClient.get<OrderActivity[]>(`/api/orders/${orderId}/activities/`);
  }

  async createOrderActivity(
    orderId: number,
    data: {
      activity_type: string;
      title: string;
      description?: string;
      activity_date?: string;
      from_status_id?: number;
      to_status_id?: number;
      contact_person_title?: string;
      contact_person_name?: string;
      contact_person_email?: string;
      contact_person_phone?: string;
    }
  ): Promise<OrderActivity> {
    return apiClient.post<OrderActivity>(`/api/orders/${orderId}/activities/`, data);
  }

  async uploadOrderActivityAttachments(
    orderId: number,
    activityId: number,
    files: File[]
  ): Promise<OrderActivityAttachment[]> {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return apiClient.postFormData<OrderActivityAttachment[]>(
      `/api/orders/${orderId}/activities/${activityId}/attachments`,
      formData
    );
  }

  async updateOrderActivity(
    orderId: number,
    activityId: number,
    data: Partial<{
      activity_type: string;
      title: string;
      description: string;
      activity_date: string;
      contact_person_name: string;
      contact_person_email: string;
      contact_person_phone: string;
      from_status_id: number;
      to_status_id: number;
    }>
  ): Promise<OrderActivity> {
    return apiClient.put<OrderActivity>(`/api/orders/${orderId}/activities/${activityId}`, data);
  }

  async deleteOrderActivity(orderId: number, activityId: number): Promise<void> {
    return apiClient.delete<void>(`/api/orders/${orderId}/activities/${activityId}`);
  }

  async deleteOrderActivityAttachment(orderId: number, activityId: number, attachmentId: number): Promise<void> {
    return apiClient.delete<void>(`/api/orders/${orderId}/activities/${activityId}/attachments/${attachmentId}`);
  }

  /** Download an order activity attachment file (uses auth; triggers browser save). */
  async downloadOrderActivityAttachment(
    orderId: number,
    activityId: number,
    attachmentId: number,
    fileName: string
  ): Promise<void> {
    const blob = await apiClient.getBlob(
      `/api/orders/${orderId}/activities/${activityId}/attachments/${attachmentId}/download`
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'attachment';
    a.click();
    URL.revokeObjectURL(url);
  }

  // Campaigns
  async getCampaigns(params?: {
    page?: number;
    page_size?: number;
    status?: string;
  }): Promise<PaginatedResponse<Campaign>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params?.page ?? 1));
    queryParams.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.status) queryParams.append('status', params.status);
    return apiClient.get<PaginatedResponse<Campaign>>(`/api/campaigns/?${queryParams.toString()}`);
  }

  async getCampaign(id: number): Promise<Campaign> {
    return apiClient.get<Campaign>(`/api/campaigns/${id}`);
  }

  async createCampaign(data: Partial<Campaign>): Promise<Campaign> {
    return apiClient.post<Campaign>('/api/campaigns/', data);
  }

  async updateCampaign(id: number, data: Partial<Campaign>): Promise<Campaign> {
    return apiClient.put<Campaign>(`/api/campaigns/${id}`, data);
  }

  async deleteCampaign(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/campaigns/${id}`);
  }

  // Domains
  async getDomains(params?: {
    page?: number;
    page_size?: number;
    is_active?: boolean;
    search?: string;
  }): Promise<PaginatedResponse<Domain>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params?.page ?? 1));
    queryParams.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.search) queryParams.append('search', params.search);
    return apiClient.get<PaginatedResponse<Domain>>(`/api/domains/?${queryParams.toString()}`);
  }

  async getDomain(id: number): Promise<Domain> {
    return apiClient.get<Domain>(`/api/domains/${id}`);
  }

  async createDomain(data: Partial<Domain>): Promise<Domain> {
    return apiClient.post<Domain>('/api/domains/', data);
  }

  async updateDomain(id: number, data: Partial<Domain>): Promise<Domain> {
    return apiClient.put<Domain>(`/api/domains/${id}`, data);
  }

  async deleteDomain(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/domains/${id}`);
  }

  // HRMS Employees (proxy from HRMS, paginated, filters)
  async getEmployees(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    department_id?: number;
    designation_id?: number;
    status?: 'all' | 'active' | 'inactive';
  }): Promise<{ employees: HRMSEmployee[]; count: number; page: number; page_size: number; total_pages: number }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.department_id != null) queryParams.append('department_id', params.department_id.toString());
    if (params?.designation_id != null) queryParams.append('designation_id', params.designation_id.toString());
    if (params?.status) queryParams.append('status', params.status);
    const query = queryParams.toString();
    return apiClient.get(query ? `/api/employees/?${query}` : '/api/employees/');
  }

  async getDepartments(): Promise<{ id: number; name: string }[]> {
    return apiClient.get<{ id: number; name: string }[]>('/api/employees/departments/');
  }

  async getDesignations(): Promise<{ id: number; title: string }[]> {
    return apiClient.get<{ id: number; title: string }[]>('/api/employees/designations/');
  }

  // Local Marketing Employees (cached table)
  async getLocalEmployees(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    role?: string;
    domain_id?: number;
    region_id?: number;
    is_active?: boolean;
    synced_from_hrms?: boolean;
  }): Promise<PaginatedResponse<MarketingEmployee>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.domain_id != null) queryParams.append('domain_id', params.domain_id.toString());
    if (params?.region_id != null) queryParams.append('region_id', params.region_id.toString());
    if (params?.is_active != null) queryParams.append('is_active', params.is_active.toString());
    if (params?.synced_from_hrms != null) queryParams.append('synced_from_hrms', params.synced_from_hrms.toString());
    const query = queryParams.toString();
    return apiClient.get(query ? `/api/employees/local/?${query}` : '/api/employees/local/');
  }

  async getLocalEmployee(id: number): Promise<MarketingEmployee> {
    return apiClient.get(`/api/employees/local/${id}`);
  }

  async updateLocalEmployee(id: number, data: Partial<MarketingEmployee>): Promise<MarketingEmployee> {
    return apiClient.put(`/api/employees/local/${id}`, data);
  }

  async syncEmployeesFromHRMS(): Promise<{
    total_hrms_employees: number;
    synced: number;
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
    employees: MarketingEmployee[];
  }> {
    return apiClient.post('/api/employees/sync');
  }

  // Regions
  async getRegions(params?: {
    page?: number;
    page_size?: number;
    domain_id?: number;
    is_active?: boolean;
    search?: string;
  }): Promise<PaginatedResponse<Region>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params?.page ?? 1));
    queryParams.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.domain_id != null) queryParams.append('domain_id', params.domain_id.toString());
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.search) queryParams.append('search', params.search);
    return apiClient.get<PaginatedResponse<Region>>(`/api/regions/?${queryParams.toString()}`);
  }

  async getRegion(id: number): Promise<Region> {
    return apiClient.get<Region>(`/api/regions/${id}`);
  }

  async createRegion(data: Partial<Region>): Promise<Region> {
    return apiClient.post<Region>('/api/regions/', data);
  }

  async updateRegion(id: number, data: Partial<Region>): Promise<Region> {
    return apiClient.put<Region>(`/api/regions/${id}`, data);
  }

  async deleteRegion(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/regions/${id}`);
  }

  async reorderRegions(domainId: number, orderedIds: number[]): Promise<{ ok: boolean; domain_id: number; count: number }> {
    return apiClient.post<{ ok: boolean; domain_id: number; count: number }>('/api/regions/reorder', {
      domain_id: domainId,
      ordered_ids: orderedIds,
    });
  }

  // Contacts
  async getContacts(params?: {
    page?: number;
    page_size?: number;
    is_active?: boolean;
    is_converted?: boolean;
    assigned_to?: number;
    search?: string;
  }): Promise<PaginatedResponse<Contact>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params?.page ?? 1));
    queryParams.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.is_converted !== undefined) queryParams.append('is_converted', params.is_converted.toString());
    if (params?.assigned_to != null) queryParams.append('assigned_to', params.assigned_to.toString());
    if (params?.search) queryParams.append('search', params.search);
    return apiClient.get<PaginatedResponse<Contact>>(`/api/contacts/?${queryParams.toString()}`);
  }

  async getContact(id: number): Promise<Contact> {
    return apiClient.get<Contact>(`/api/contacts/${id}`);
  }

  /** Search contacts by email, phone, name, or company. For lead form: link lead to existing or create new contact. */
  async searchContacts(q: string, limit?: number): Promise<Contact[]> {
    const params = new URLSearchParams({ q: q.trim() });
    if (limit != null) params.append('limit', String(limit));
    return apiClient.get<Contact[]>(`/api/contacts/search?${params.toString()}`);
  }

  async createContact(data: Partial<Contact>): Promise<Contact> {
    return apiClient.post<Contact>('/api/contacts/', data);
  }

  async updateContact(id: number, data: Partial<Contact>): Promise<Contact> {
    return apiClient.put<Contact>(`/api/contacts/${id}`, data);
  }

  async deleteContact(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/contacts/${id}`);
  }

  async convertContactToCustomer(contactId: number): Promise<{ success: boolean; customer_id: number; contact_id: number }> {
    return apiClient.post<{ success: boolean; customer_id: number; contact_id: number }>(`/api/contacts/${contactId}/convert-to-customer`);
  }

  // Visiting Card Contacts
  async getVisitingCardContacts(params?: {
    page?: number;
    page_size?: number;
    is_converted?: boolean;
    search?: string;
  }): Promise<PaginatedResponse<VisitingCardContact>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params?.page ?? 1));
    queryParams.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.is_converted !== undefined) queryParams.append('is_converted', params.is_converted.toString());
    if (params?.search) queryParams.append('search', params.search);
    return apiClient.get<PaginatedResponse<VisitingCardContact>>(`/api/visiting-card-contacts/?${queryParams.toString()}`);
  }

  async getVisitingCardContact(id: number): Promise<VisitingCardContact> {
    return apiClient.get<VisitingCardContact>(`/api/visiting-card-contacts/${id}`);
  }

  async createVisitingCardContact(data: Partial<VisitingCardContact>): Promise<VisitingCardContact> {
    return apiClient.post<VisitingCardContact>('/api/visiting-card-contacts/', data);
  }

  async updateVisitingCardContact(id: number, data: Partial<VisitingCardContact>): Promise<VisitingCardContact> {
    return apiClient.put<VisitingCardContact>(`/api/visiting-card-contacts/${id}`, data);
  }

  async deleteVisitingCardContact(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/visiting-card-contacts/${id}`);
  }

  async convertVisitingCardContactToContact(
    id: number,
    data?: { domain_id?: number; region_id?: number; organization_id?: number; plant_id?: number }
  ): Promise<{ success: boolean; contact_id: number; visiting_card_contact_id: number }> {
    return apiClient.post<{ success: boolean; contact_id: number; visiting_card_contact_id: number }>(
      `/api/visiting-card-contacts/${id}/convert-to-contact`,
      data ?? {}
    );
  }

  // Customers
  async getCustomers(params?: {
    page?: number;
    page_size?: number;
    domain_id?: number;
    region_id?: number;
    is_active?: boolean;
  }): Promise<PaginatedResponse<Customer>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params?.page ?? 1));
    queryParams.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.domain_id != null) queryParams.append('domain_id', params.domain_id.toString());
    if (params?.region_id != null) queryParams.append('region_id', params.region_id.toString());
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    return apiClient.get<PaginatedResponse<Customer>>(`/api/customers/?${queryParams.toString()}`);
  }

  async getCustomer(id: number): Promise<Customer> {
    return apiClient.get<Customer>(`/api/customers/${id}`);
  }

  /** Search customers by primary contact email, phone, or company name. For lead form. */
  async searchCustomers(q: string, limit?: number): Promise<Customer[]> {
    const params = new URLSearchParams({ q: q.trim() });
    if (limit != null) params.append('limit', String(limit));
    return apiClient.get<Customer[]>(`/api/customers/search?${params.toString()}`);
  }

  async createCustomer(data: Partial<Customer>): Promise<Customer> {
    return apiClient.post<Customer>('/api/customers/', data);
  }

  async updateCustomer(id: number, data: Partial<Customer>): Promise<Customer> {
    return apiClient.put<Customer>(`/api/customers/${id}`, data);
  }

  async deleteCustomer(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/customers/${id}`);
  }

  // ── Service module — Stage 1: Contracts ──
  async getServiceContracts(params?: {
    page?: number;
    page_size?: number;
    customer_id?: number;
    plant_id?: number;
    contract_type?: ServiceContractType;
    status?: ServiceContractStatus;
    search?: string;
  }): Promise<PaginatedResponse<ServiceContract>> {
    const q = new URLSearchParams();
    q.append('page', String(params?.page ?? 1));
    q.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.customer_id != null) q.append('customer_id', String(params.customer_id));
    if (params?.plant_id != null) q.append('plant_id', String(params.plant_id));
    if (params?.contract_type) q.append('contract_type', params.contract_type);
    if (params?.status) q.append('status', params.status);
    if (params?.search?.trim()) q.append('search', params.search.trim());
    return apiClient.get<PaginatedResponse<ServiceContract>>(`/api/service/contracts/?${q.toString()}`);
  }

  async getServiceContract(id: number): Promise<ServiceContract> {
    return apiClient.get<ServiceContract>(`/api/service/contracts/${id}`);
  }

  async createServiceContract(data: ServiceContractPayload): Promise<ServiceContract> {
    return apiClient.post<ServiceContract>('/api/service/contracts/', data);
  }

  async updateServiceContract(id: number, data: Partial<ServiceContractPayload>): Promise<ServiceContract> {
    return apiClient.put<ServiceContract>(`/api/service/contracts/${id}`, data);
  }

  async deleteServiceContract(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/service/contracts/${id}`);
  }

  // ── Service module — Stage 2: Plans & Visits ──
  async getServicePlanForContract(contractId: number): Promise<ServicePlan | null> {
    return apiClient.get<ServicePlan | null>(`/api/service/contracts/${contractId}/plan`);
  }

  async getServicePlan(id: number): Promise<ServicePlan> {
    return apiClient.get<ServicePlan>(`/api/service/plans/${id}`);
  }

  async createServicePlan(data: {
    contract_id: number;
    scheduled_visits_planned?: number;
    preferred_schedule_note?: string | null;
    notes?: string | null;
  }): Promise<ServicePlan> {
    return apiClient.post<ServicePlan>('/api/service/plans/', data);
  }

  async updateServicePlan(
    id: number,
    data: { scheduled_visits_planned?: number; preferred_schedule_note?: string | null; notes?: string | null },
  ): Promise<ServicePlan> {
    return apiClient.put<ServicePlan>(`/api/service/plans/${id}`, data);
  }

  async generateScheduledVisits(planId: number): Promise<{ created: number; total_scheduled: number; visits: ServiceVisit[] }> {
    return apiClient.post(`/api/service/plans/${planId}/generate-visits`, {});
  }

  async getServiceVisits(params?: {
    plan_id?: number;
    contract_id?: number;
    complaint_id?: number;
    customer_id?: number;
    status?: ServiceVisitStatus;
    kind?: ServiceVisitKind;
    date_from?: string;
    date_to?: string;
  }): Promise<ServiceVisit[]> {
    const q = new URLSearchParams();
    if (params?.plan_id != null) q.append('plan_id', String(params.plan_id));
    if (params?.contract_id != null) q.append('contract_id', String(params.contract_id));
    if (params?.complaint_id != null) q.append('complaint_id', String(params.complaint_id));
    if (params?.customer_id != null) q.append('customer_id', String(params.customer_id));
    if (params?.status) q.append('status', params.status);
    if (params?.kind) q.append('kind', params.kind);
    if (params?.date_from) q.append('date_from', params.date_from);
    if (params?.date_to) q.append('date_to', params.date_to);
    const qs = q.toString();
    return apiClient.get<ServiceVisit[]>(`/api/service/visits/${qs ? `?${qs}` : ''}`);
  }

  async createServiceVisit(data: {
    plan_id: number;
    kind?: ServiceVisitKind;
    title?: string;
    planned_date?: string | null;
    scheduled_date?: string | null;
    assigned_engineer_employee_id?: number | null;
    assigned_engineer_username?: string | null;
    notes?: string | null;
  }): Promise<ServiceVisit> {
    return apiClient.post<ServiceVisit>('/api/service/visits/', data);
  }

  async updateServiceVisit(
    id: number,
    data: {
      title?: string | null;
      status?: ServiceVisitStatus;
      planned_date?: string | null;
      scheduled_date?: string | null;
      assigned_engineer_employee_id?: number | null;
      assigned_engineer_username?: string | null;
      notes?: string | null;
    },
  ): Promise<ServiceVisit> {
    return apiClient.put<ServiceVisit>(`/api/service/visits/${id}`, data);
  }

  async rescheduleServiceVisit(id: number, newDate: string, reason: string): Promise<ServiceVisit> {
    return apiClient.post<ServiceVisit>(`/api/service/visits/${id}/reschedule`, { new_date: newDate, reason });
  }

  async completeServiceVisit(id: number): Promise<ServiceVisit> {
    return apiClient.post<ServiceVisit>(`/api/service/visits/${id}/complete`, {});
  }

  async deleteServiceVisit(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/service/visits/${id}`);
  }

  // ── Service module — Stage 3: Work Orders ──
  async getServiceWorkOrders(params?: {
    status?: ServiceWorkOrderStatus;
    contract_id?: number;
    visit_id?: number;
    customer_id?: number;
  }): Promise<ServiceWorkOrder[]> {
    const q = new URLSearchParams();
    if (params?.status) q.append('status', params.status);
    if (params?.contract_id != null) q.append('contract_id', String(params.contract_id));
    if (params?.visit_id != null) q.append('visit_id', String(params.visit_id));
    if (params?.customer_id != null) q.append('customer_id', String(params.customer_id));
    const qs = q.toString();
    return apiClient.get<ServiceWorkOrder[]>(`/api/service/work-orders/${qs ? `?${qs}` : ''}`);
  }

  async getWorkOrderForContract(contractId: number): Promise<ServiceWorkOrder | null> {
    return apiClient.get<ServiceWorkOrder | null>(`/api/service/contracts/${contractId}/work-order`);
  }

  async getWorkOrderForVisit(visitId: number): Promise<ServiceWorkOrder | null> {
    return apiClient.get<ServiceWorkOrder | null>(`/api/service/visits/${visitId}/work-order`);
  }

  async getServiceWorkOrder(id: number): Promise<ServiceWorkOrder> {
    return apiClient.get<ServiceWorkOrder>(`/api/service/work-orders/${id}`);
  }

  async createServiceWorkOrder(data: ServiceWorkOrderPayload): Promise<ServiceWorkOrder> {
    return apiClient.post<ServiceWorkOrder>('/api/service/work-orders/', data);
  }

  async updateServiceWorkOrder(id: number, data: Partial<ServiceWorkOrderPayload>): Promise<ServiceWorkOrder> {
    return apiClient.put<ServiceWorkOrder>(`/api/service/work-orders/${id}`, data);
  }

  async deleteServiceWorkOrder(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/service/work-orders/${id}`);
  }

  async workOrderMarkChecked(id: number): Promise<ServiceWorkOrder> {
    return apiClient.post<ServiceWorkOrder>(`/api/service/work-orders/${id}/mark-checked`, {});
  }
  async workOrderMarkPrepared(id: number, reason: string): Promise<ServiceWorkOrder> {
    return apiClient.post<ServiceWorkOrder>(`/api/service/work-orders/${id}/mark-prepared`, { reason });
  }
  async workOrderApprove(id: number): Promise<ServiceWorkOrder> {
    return apiClient.post<ServiceWorkOrder>(`/api/service/work-orders/${id}/approve`, {});
  }
  async workOrderSendBack(id: number, reason: string): Promise<ServiceWorkOrder> {
    return apiClient.post<ServiceWorkOrder>(`/api/service/work-orders/${id}/send-back`, { reason });
  }
  async workOrderSubmit(id: number): Promise<ServiceWorkOrder> {
    return apiClient.post<ServiceWorkOrder>(`/api/service/work-orders/${id}/submit`, {});
  }
  async workOrderReopen(id: number, reason: string): Promise<ServiceWorkOrder> {
    return apiClient.post<ServiceWorkOrder>(`/api/service/work-orders/${id}/reopen`, { reason });
  }
  async workOrderSendPrerequisites(id: number): Promise<ServiceWorkOrder> {
    return apiClient.post<ServiceWorkOrder>(`/api/service/work-orders/${id}/send-prerequisites`, {});
  }

  async addMaterialDispatch(materialId: number, data: WorkOrderDispatchInput): Promise<WorkOrderMaterial> {
    return apiClient.post<WorkOrderMaterial>(`/api/service/materials/${materialId}/dispatches`, data);
  }

  async deleteMaterialDispatch(materialId: number, dispatchId: number): Promise<WorkOrderMaterial> {
    return apiClient.delete<WorkOrderMaterial>(`/api/service/materials/${materialId}/dispatches/${dispatchId}`);
  }

  async setMaterialDispatch(
    materialId: number,
    dispatch_status: ServiceDispatchStatus,
    dispatched_note?: string | null,
  ): Promise<WorkOrderMaterial> {
    return apiClient.post<WorkOrderMaterial>(`/api/service/materials/${materialId}/dispatch`, {
      dispatch_status,
      dispatched_note: dispatched_note ?? null,
    });
  }

  async uploadMaterialProof(materialId: number, files: File[]): Promise<WorkOrderMaterialAttachment[]> {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return apiClient.postFormData<WorkOrderMaterialAttachment[]>(`/api/service/materials/${materialId}/attachments`, fd);
  }

  async uploadWorkOrderFiles(workOrderId: number, files: File[], visitId?: number | null): Promise<WorkOrderFile[]> {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    const q = visitId != null ? `?visit_id=${visitId}` : '';
    return apiClient.postFormData<WorkOrderFile[]>(`/api/service/work-orders/${workOrderId}/files${q}`, fd);
  }

  async downloadWorkOrderFile(workOrderId: number, fileId: number, fileName: string): Promise<void> {
    const blob = await apiClient.getBlob(`/api/service/work-orders/${workOrderId}/files/${fileId}/download`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'work-order-file';
    a.click();
    URL.revokeObjectURL(url);
  }

  async deleteWorkOrderFile(workOrderId: number, fileId: number): Promise<void> {
    return apiClient.delete<void>(`/api/service/work-orders/${workOrderId}/files/${fileId}`);
  }

  async downloadMaterialProof(materialId: number, attachmentId: number, fileName: string): Promise<void> {
    const blob = await apiClient.getBlob(`/api/service/materials/${materialId}/attachments/${attachmentId}/download`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'attachment';
    a.click();
    URL.revokeObjectURL(url);
  }

  async deleteMaterialProof(materialId: number, attachmentId: number): Promise<void> {
    return apiClient.delete<void>(`/api/service/materials/${materialId}/attachments/${attachmentId}`);
  }

  async confirmPrerequisite(prereqId: number, is_done: boolean): Promise<WorkOrderPrerequisite> {
    return apiClient.post<WorkOrderPrerequisite>(`/api/service/prerequisites/${prereqId}/confirm`, { is_done });
  }

  async getStoreMaterialQueue(): Promise<ServiceWorkOrder[]> {
    return apiClient.get<ServiceWorkOrder[]>('/api/service/store/material-queue');
  }

  // ── Service module — Stage 4: Complaints ──
  async getServiceComplaints(params?: {
    status?: ServiceComplaintStatus;
    issue_type?: ServiceIssueType;
    customer_id?: number;
    plant_id?: number;
    assignee_employee_id?: number;
    source?: ServiceComplaintSource;
    search?: string;
  }): Promise<ServiceComplaint[]> {
    const q = new URLSearchParams();
    if (params?.status) q.append('status', params.status);
    if (params?.issue_type) q.append('issue_type', params.issue_type);
    if (params?.customer_id != null) q.append('customer_id', String(params.customer_id));
    if (params?.plant_id != null) q.append('plant_id', String(params.plant_id));
    if (params?.assignee_employee_id != null) q.append('assignee_employee_id', String(params.assignee_employee_id));
    if (params?.source) q.append('source', params.source);
    if (params?.search?.trim()) q.append('search', params.search.trim());
    const qs = q.toString();
    return apiClient.get<ServiceComplaint[]>(`/api/service/complaints/${qs ? `?${qs}` : ''}`);
  }

  async getServiceComplaint(id: number): Promise<ServiceComplaint> {
    return apiClient.get<ServiceComplaint>(`/api/service/complaints/${id}`);
  }

  async createServiceComplaint(data: ServiceComplaintPayload): Promise<ServiceComplaint> {
    return apiClient.post<ServiceComplaint>('/api/service/complaints/', data);
  }

  async updateServiceComplaint(
    id: number,
    data: { issue_type?: ServiceIssueType; title?: string; description?: string | null; plant_id?: number; contract_id?: number | null; planned_time_hours?: number | null; actual_time_hours?: number | null },
  ): Promise<ServiceComplaint> {
    return apiClient.put<ServiceComplaint>(`/api/service/complaints/${id}`, data);
  }

  /** Assign a complaint to an employee, a department, or both */
  async getServiceReportSummary(params: {
    group_by: 'company' | 'equipment' | 'person' | 'department';
    date_from?: string;
    date_to?: string;
  }): Promise<ServiceReportSummary> {
    const q = new URLSearchParams({ group_by: params.group_by });
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to) q.append('date_to', params.date_to);
    return apiClient.get<ServiceReportSummary>(`/api/service/report-summary?${q.toString()}`);
  }

  async getServiceIssueTypes(): Promise<ServiceIssueTypeOption[]> {
    return apiClient.get<ServiceIssueTypeOption[]>('/api/service/complaints/meta/issue-types');
  }

  async createServiceIssueType(label: string): Promise<ServiceIssueTypeOption> {
    return apiClient.post<ServiceIssueTypeOption>('/api/service/complaints/meta/issue-types', { label });
  }

  async assignServiceComplaint(
    id: number,
    data: { assignee_employee_id?: number | null; assignee_username?: string | null; assignee_department_id?: number | null; assignee_department_name?: string | null; note?: string },
  ): Promise<ServiceComplaint> {
    return apiClient.post<ServiceComplaint>(`/api/service/complaints/${id}/assign`, data);
  }

  async setServiceComplaintStatus(id: number, statusVal: 'open' | 'in_progress' | 'resolved', note?: string): Promise<ServiceComplaint> {
    return apiClient.post<ServiceComplaint>(`/api/service/complaints/${id}/status`, { status: statusVal, note });
  }

  async approveServiceComplaint(id: number): Promise<ServiceComplaint> {
    return apiClient.post<ServiceComplaint>(`/api/service/complaints/${id}/approve`, {});
  }

  async reopenServiceComplaint(id: number, note: string): Promise<ServiceComplaint> {
    return apiClient.post<ServiceComplaint>(`/api/service/complaints/${id}/reopen`, { note });
  }

  async closeServiceComplaint(id: number, note?: string, actual_time_hours?: number | null): Promise<ServiceComplaint> {
    return apiClient.post<ServiceComplaint>(`/api/service/complaints/${id}/close`, { note, actual_time_hours });
  }

  async commentServiceComplaint(id: number, note: string): Promise<ServiceComplaint> {
    return apiClient.post<ServiceComplaint>(`/api/service/complaints/${id}/comment`, { note });
  }

  async scheduleVisitForComplaint(
    id: number,
    data: {
      title?: string;
      planned_date?: string | null;
      scheduled_date?: string | null;
      assigned_engineer_employee_id?: number | null;
      assigned_engineer_username?: string | null;
      notes?: string | null;
    },
  ): Promise<ServiceVisit> {
    return apiClient.post<ServiceVisit>(`/api/service/complaints/${id}/visits`, data);
  }

  async deleteServiceComplaint(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/service/complaints/${id}`);
  }

  // ── Service module — Stage 5: visit execution, calibration, PO variance, reports ──
  async getVisitReportBundle(visitId: number): Promise<VisitReportBundle> {
    return apiClient.get<VisitReportBundle>(`/api/service/visits/${visitId}/report-bundle`);
  }

  async uploadVisitAttachments(visitId: number, files: File[], caption?: string): Promise<VisitAttachment[]> {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    if (caption) fd.append('caption', caption);
    return apiClient.postFormData<VisitAttachment[]>(`/api/service/visits/${visitId}/attachments`, fd);
  }
  async downloadVisitAttachment(visitId: number, attachmentId: number, fileName: string): Promise<void> {
    const blob = await apiClient.getBlob(`/api/service/visits/${visitId}/attachments/${attachmentId}/download`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'attachment';
    a.click();
    URL.revokeObjectURL(url);
  }
  async deleteVisitAttachment(visitId: number, attachmentId: number): Promise<void> {
    return apiClient.delete<void>(`/api/service/visits/${visitId}/attachments/${attachmentId}`);
  }

  async addCalibration(visitId: number, data: Omit<ServiceCalibration, 'id' | 'visit_id' | 'created_at'>): Promise<ServiceCalibration> {
    return apiClient.post<ServiceCalibration>(`/api/service/visits/${visitId}/calibrations`, data);
  }
  async updateCalibration(id: number, data: Omit<ServiceCalibration, 'id' | 'visit_id' | 'created_at'>): Promise<ServiceCalibration> {
    return apiClient.put<ServiceCalibration>(`/api/service/calibrations/${id}`, data);
  }
  async deleteCalibration(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/service/calibrations/${id}`);
  }

  async addPOVariance(
    visitId: number,
    data: { po_requirement: string; actual_requirement: string; additional_charge?: number | null },
  ): Promise<ServicePOVariance> {
    return apiClient.post<ServicePOVariance>(`/api/service/visits/${visitId}/po-variances`, data);
  }
  async recordPOVarianceResponse(id: number, customer_response: 'accepted' | 'rejected', response_note?: string): Promise<ServicePOVariance> {
    return apiClient.post<ServicePOVariance>(`/api/service/po-variances/${id}/record-response`, { customer_response, response_note });
  }
  async sendPOVarianceEmail(id: number): Promise<ServicePOVariance> {
    return apiClient.post<ServicePOVariance>(`/api/service/po-variances/${id}/send-email`, {});
  }
  async deletePOVariance(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/service/po-variances/${id}`);
  }

  async getServiceReport(visitId: number): Promise<ServiceReport | null> {
    return apiClient.get<ServiceReport | null>(`/api/service/visits/${visitId}/report`);
  }
  async saveServiceReport(visitId: number, data: { summary?: string | null; imported_data?: string | null }): Promise<ServiceReport> {
    return apiClient.put<ServiceReport>(`/api/service/visits/${visitId}/report`, data);
  }
  async submitServiceReport(visitId: number): Promise<ServiceReport> {
    return apiClient.post<ServiceReport>(`/api/service/visits/${visitId}/report/submit`, {});
  }
  async sendReportToAccounts(reportId: number): Promise<ServiceReport> {
    return apiClient.post<ServiceReport>(`/api/service/reports/${reportId}/send-to-accounts`, {});
  }
  async sendFeedbackEmail(reportId: number): Promise<ServiceReport> {
    return apiClient.post<ServiceReport>(`/api/service/reports/${reportId}/send-feedback`, {});
  }

  // Organizations
  async getOrganizations(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    is_active?: boolean;
  }): Promise<PaginatedResponse<Organization>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params?.page ?? 1));
    queryParams.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.search) queryParams.append('search', params.search);
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    return apiClient.get<PaginatedResponse<Organization>>(`/api/organizations/?${queryParams.toString()}`);
  }

  async getOrganization(id: number): Promise<Organization> {
    return apiClient.get<Organization>(`/api/organizations/${id}`);
  }

  /** Create organization; optionally pass plants to create in the same request. */
  async createOrganization(data: Partial<Organization> & { plants?: Array<Partial<Plant>> }): Promise<Organization> {
    return apiClient.post<Organization>('/api/organizations/', data);
  }

  async updateOrganization(id: number, data: Partial<Organization>): Promise<Organization> {
    return apiClient.patch<Organization>(`/api/organizations/${id}`, data);
  }

  async deleteOrganization(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/organizations/${id}`);
  }

  async getOrganizationPlants(organizationId: number): Promise<Plant[]> {
    return apiClient.get<Plant[]>(`/api/organizations/${organizationId}/plants`);
  }

  /** Payload for creating a plant under an organization (only these fields are sent). */
  buildCreatePlantPayload(data: Partial<Plant>): {
    plant_name: string;
    plant_code?: string;
    domain_id?: number;
    region_id?: number;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    country?: string;
    postal_code?: string;
    notes?: string;
  } {
    const name = (data.plant_name ?? '').toString().trim();
    const out: ReturnType<typeof marketingAPI.buildCreatePlantPayload> = {
      plant_name: name,
    };
    if (data.plant_code != null && String(data.plant_code).trim()) out.plant_code = String(data.plant_code).trim();
    if (data.domain_id != null && !isNaN(Number(data.domain_id))) out.domain_id = Number(data.domain_id);
    if (data.region_id != null && !isNaN(Number(data.region_id))) out.region_id = Number(data.region_id);
    if (data.address_line1 != null && String(data.address_line1).trim()) out.address_line1 = String(data.address_line1).trim();
    if (data.address_line2 != null && String(data.address_line2).trim()) out.address_line2 = String(data.address_line2).trim();
    if (data.city != null && String(data.city).trim()) out.city = String(data.city).trim();
    if (data.state != null && String(data.state).trim()) out.state = String(data.state).trim();
    if (data.country != null && String(data.country).trim()) out.country = String(data.country).trim();
    if (data.postal_code != null && String(data.postal_code).trim()) out.postal_code = String(data.postal_code).trim();
    if (data.notes != null && String(data.notes).trim()) out.notes = String(data.notes).trim();
    return out;
  }

  async createOrganizationPlant(organizationId: number, data: Partial<Plant>): Promise<Plant> {
    const payload = this.buildCreatePlantPayload(data);
    if (!payload.plant_name) {
      throw new Error('Plant name is required');
    }
    return apiClient.post<Plant>(`/api/organizations/${organizationId}/plants`, payload);
  }

  async updateOrganizationPlant(organizationId: number, plantId: number, data: Partial<Plant>): Promise<Plant> {
    return apiClient.patch<Plant>(`/api/organizations/${organizationId}/plants/${plantId}`, data);
  }

  async deleteOrganizationPlant(organizationId: number, plantId: number): Promise<void> {
    return apiClient.delete<void>(`/api/organizations/${organizationId}/plants/${plantId}`);
  }

  // Plants
  async getPlants(params?: {
    contact_id?: number;
    customer_id?: number;
  }): Promise<Plant[]> {
    const queryParams = new URLSearchParams();
    if (params?.contact_id) queryParams.append('contact_id', params.contact_id.toString());
    if (params?.customer_id) queryParams.append('customer_id', params.customer_id.toString());
    const query = queryParams.toString();
    return apiClient.get<Plant[]>(query ? `/api/plants/?${query}` : '/api/plants/');
  }

  async getPlant(id: number): Promise<Plant> {
    return apiClient.get<Plant>(`/api/plants/${id}`);
  }

  async createPlant(data: Partial<Plant>): Promise<Plant> {
    return apiClient.post<Plant>('/api/plants/', data);
  }

  async updatePlant(id: number, data: Partial<Plant>): Promise<Plant> {
    return apiClient.put<Plant>(`/api/plants/${id}`, data);
  }

  async deletePlant(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/plants/${id}`);
  }

  // Employee Region Assignments
  /** All active assignments with employee_name/employee_email from HRMS (no extra employees call needed). */
  async getAllAssignments(): Promise<AssignmentWithEmployee[]> {
    return apiClient.get<AssignmentWithEmployee[]>('/api/regions/assignments/');
  }

  async getEmployeeAssignments(employeeId: number): Promise<any[]> {
    return apiClient.get<any[]>(`/api/regions/assignments/${employeeId}`);
  }

  async assignEmployeeToRegion(data: {
    employee_id: number;
    region_id: number;
    role: 'head' | 'employee' | 'supervisor' | 'coordinator';
    employee_name?: string;
    employee_email?: string;
  }): Promise<any> {
    return apiClient.post<any>('/api/regions/assign-employee', data);
  }

  async updateEmployeeAssignment(
    assignmentId: number,
    data: { role?: 'head' | 'employee' | 'supervisor' | 'coordinator'; is_active?: boolean }
  ): Promise<AssignmentWithEmployee> {
    return apiClient.put<AssignmentWithEmployee>(`/api/regions/assignments/${assignmentId}`, data);
  }

  async removeEmployeeFromRegion(assignmentId: number): Promise<void> {
    return apiClient.delete<void>(`/api/regions/assignments/${assignmentId}`);
  }

  // Numbering Series
  async getSeries(params?: {
    page?: number;
    page_size?: number;
    is_active?: boolean;
    entity_type?: string;
    search?: string;
    order_by?: string;
    order_dir?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Series>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params?.page ?? 1));
    queryParams.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
    if (params?.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.order_by) queryParams.append('order_by', params.order_by);
    if (params?.order_dir) queryParams.append('order_dir', params.order_dir);
    return apiClient.get<PaginatedResponse<Series>>(`/api/series/?${queryParams.toString()}`);
  }

  async getSeriesById(id: number): Promise<Series> {
    return apiClient.get<Series>(`/api/series/${id}`);
  }

  async createSeries(data: SeriesCreateInput): Promise<Series> {
    return apiClient.post<Series>('/api/series/', data);
  }

  async updateSeries(id: number, data: SeriesUpdateInput): Promise<Series> {
    return apiClient.put<Series>(`/api/series/${id}`, data);
  }

  async deleteSeries(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/series/${id}`);
  }

  async generateNextSeriesNumber(
    seriesId: number,
    context?: { customer_id?: number; contact_id?: number; lead_id?: number },
    preview = false
  ): Promise<SeriesGenerateResponse> {
    return apiClient.post<SeriesGenerateResponse>(
      `/api/series/${seriesId}/generate-next${preview ? '?preview=1' : ''}`,
      context ?? {}
    );
  }

  /** Generate next value by series code (character field, not FK). Pass lead_id/contact_id/customer_id or lead_context (company) for pattern placeholders like {lead.company}. Pass preview=true to only peek at the next value without consuming it (the counter is advanced on actual save). */
  async generateNextSeriesNumberByCode(
    seriesCode: string,
    context?: {
      customer_id?: number;
      contact_id?: number;
      lead_id?: number;
      lead_context?: { company?: string; company_slug?: string };
    },
    preview = false
  ): Promise<SeriesGenerateResponse> {
    return apiClient.post<SeriesGenerateResponse>(
      `/api/series/generate-next${preview ? '?preview=1' : ''}`,
      { series_code: seriesCode, ...context }
    );
  }

  // Profile & Connect Email (uses same API base + auth token)
  async getAuthProfile(): Promise<{ success: boolean; user?: any; employee?: any; roles?: any[] }> {
    return apiClient.get<{ success: boolean; user?: any; employee?: any; roles?: any[] }>('/api/auth/me');
  }

  async getEmailConnection(): Promise<{ connected: boolean; email?: string }> {
    return apiClient.get<{ connected: boolean; email?: string }>('/api/auth/email-connection');
  }

  async getEmailAuthorizeUrl(): Promise<{ url: string }> {
    return apiClient.get<{ url: string }>('/api/auth/email/authorize-url');
  }

  async disconnectEmail(): Promise<{ success: boolean; connected: boolean }> {
    return apiClient.delete<{ success: boolean; connected: boolean }>('/api/auth/email');
  }

  /** Invalidate this token's server-side RBAC permission cache; next request refetches from HRMS. */
  async refreshPermissions(): Promise<{ success: boolean; message?: string }> {
    return apiClient.post<{ success: boolean; message?: string }>('/api/auth/refresh-permissions');
  }

  /** List quotations with optional search, lead filter, date range, industry, series, region/domain (super admin only), and sort (API-side). */
  async getMyQuotations(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    lead_id?: number;
    date_from?: string;
    date_to?: string;
    industry?: string;
    quote_series_code?: string;
    region_id?: number;
    domain_id?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<QuotationListItem>> {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(params?.page ?? 1));
    queryParams.set('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.search?.trim()) queryParams.set('search', params.search.trim());
    if (params?.lead_id != null) queryParams.set('lead_id', String(params.lead_id));
    if (params?.date_from?.trim()) queryParams.set('date_from', params.date_from.trim());
    if (params?.date_to?.trim()) queryParams.set('date_to', params.date_to.trim());
    if (params?.industry?.trim()) queryParams.set('industry', params.industry.trim());
    if (params?.quote_series_code?.trim()) queryParams.set('quote_series_code', params.quote_series_code.trim());
    if (params?.region_id != null) queryParams.set('region_id', String(params.region_id));
    if (params?.domain_id != null) queryParams.set('domain_id', String(params.domain_id));
    if (params?.sort_by) queryParams.set('sort_by', params.sort_by);
    if (params?.sort_order) queryParams.set('sort_order', params.sort_order);
    return apiClient.get<PaginatedResponse<QuotationListItem>>(`/api/quotations/?${queryParams.toString()}`);
  }

  /** Distinct industry / numbering-series values available for the quotation filters, scoped like getMyQuotations. */
  async getQuotationFilterOptions(): Promise<QuotationFilterOptions> {
    return apiClient.get<QuotationFilterOptions>('/api/quotations/filter-options');
  }

  /** Reports: who the current user can run reports for (self, or team if region/domain head) */
  async getReportsScope(): Promise<ReportScopeResponse> {
    return apiClient.get<ReportScopeResponse>('/api/reports/scope');
  }

  /** Reports: summary metrics for selected employee (or self) and optional date range */
  async getReportsSummary(params?: {
    date_from?: string;
    date_to?: string;
    employee_id?: number;
  }): Promise<ReportSummaryResponse> {
    const searchParams = new URLSearchParams();
    if (params?.date_from) searchParams.set('date_from', params.date_from);
    if (params?.date_to) searchParams.set('date_to', params.date_to);
    if (params?.employee_id != null) searchParams.set('employee_id', String(params.employee_id));
    const qs = searchParams.toString();
    return apiClient.get<ReportSummaryResponse>(`/api/reports/summary${qs ? `?${qs}` : ''}`);
  }

  /** Expected order report: create (select leads as next month potential clients) */
  async createExpectedOrderReport(data: { year: number; month: number; lead_ids: number[] }): Promise<ExpectedOrderReportItem> {
    return apiClient.post<ExpectedOrderReportItem>('/api/reports/expected-orders', data);
  }

  /** Ids of the leads already in YOUR expected order for that month (so the picker can grey them out) */
  async getPlannedLeadIds(year: number, month: number): Promise<number[]> {
    return apiClient.get<number[]>(`/api/reports/expected-orders/planned-leads?year=${year}&month=${month}`);
  }

  /** Expected order reports: list (optional year/month/employee_id filter; employee_id respects report scope) */
  async listExpectedOrderReports(params?: { year?: number; month?: number; employee_id?: number }): Promise<ExpectedOrderReportItem[]> {
    const sp = new URLSearchParams();
    if (params?.year != null) sp.set('year', String(params.year));
    if (params?.month != null) sp.set('month', String(params.month));
    if (params?.employee_id != null) sp.set('employee_id', String(params.employee_id));
    const qs = sp.toString();
    return apiClient.get<ExpectedOrderReportItem[]>(`/api/reports/expected-orders${qs ? `?${qs}` : ''}`);
  }

  /** MIS: one employee's month of work (defaults to you; employee_id must be in your report scope) */
  async getMISForEmployee(year: number, month: number, employee_id?: number): Promise<MISEmployeeResponse> {
    const sp = new URLSearchParams({ year: String(year), month: String(month) });
    if (employee_id != null) sp.set('employee_id', String(employee_id));
    return apiClient.get<MISEmployeeResponse>(`/api/reports/mis?${sp.toString()}`);
  }

  /** MIS team overview: one row per person in your scope (empty for people with no team) */
  async getMISTeam(year: number, month: number): Promise<MISTeamResponse> {
    return apiClient.get<MISTeamResponse>(`/api/reports/mis/team?year=${year}&month=${month}`);
  }

  /** OD plan reports: list (optional year/month/employee_id filter; employee_id respects report scope) */
  async listODPlanReports(params?: { year?: number; month?: number; employee_id?: number }): Promise<ODPlanReportItem[]> {
    const sp = new URLSearchParams();
    if (params?.year != null) sp.set('year', String(params.year));
    if (params?.month != null) sp.set('month', String(params.month));
    if (params?.employee_id != null) sp.set('employee_id', String(params.employee_id));
    const qs = sp.toString();
    return apiClient.get<ODPlanReportItem[]>(`/api/reports/od-plans${qs ? `?${qs}` : ''}`);
  }

  /** OD plan: get one for year-month */
  async getODPlanReport(year: number, month: number): Promise<ODPlanReportItem> {
    return apiClient.get<ODPlanReportItem>(`/api/reports/od-plans/${year}/${month}`);
  }

  /** OD plan: add a visit that was NOT in the plan (today or earlier in that month; works after the deadline) */
  async addUnplannedVisit(year: number, month: number, entry: ODPlanEntryCreate): Promise<ODPlanEntryItem> {
    return apiClient.post<ODPlanEntryItem>(`/api/reports/od-plans/${year}/${month}/unplanned`, entry);
  }

  /** OD plan: remove one of your own unplanned visits */
  async deleteUnplannedVisit(entryId: number): Promise<void> {
    await apiClient.delete(`/api/reports/od-plans/unplanned/${entryId}`);
  }

  /** OD plan: save (create or replace entries for year-month) */
  async saveODPlanReport(year: number, month: number, data: { entries: ODPlanEntryCreate[] }): Promise<ODPlanReportItem> {
    return apiClient.put<ODPlanReportItem>(`/api/reports/od-plans/${year}/${month}`, data);
  }

  /** Dashboard: monthly target vs achieved, won/lost counts (for current user or employee_id if admin/head) */
  async getDashboardTargetStats(params?: { employee_id?: number; date_from?: string; date_to?: string }): Promise<DashboardTargetStats> {
    const sp = new URLSearchParams();
    if (params?.employee_id != null) sp.set('employee_id', String(params.employee_id));
    if (params?.date_from?.trim()) sp.set('date_from', params.date_from.trim());
    if (params?.date_to?.trim()) sp.set('date_to', params.date_to.trim());
    const qs = sp.toString();
    return apiClient.get<DashboardTargetStats>(`/api/dashboard/target-stats${qs ? `?${qs}` : ''}`);
  }

  /** Scope-wide target/achieved for dashboard. For heads returns aggregated team stats. */
  async getScopeTargetStats(params?: {
    date_from?: string;
    date_to?: string;
    domain_id?: number;
    region_id?: number;
  }): Promise<ScopeTargetStats> {
    const sp = new URLSearchParams();
    if (params?.date_from?.trim()) sp.set('date_from', params.date_from.trim());
    if (params?.date_to?.trim()) sp.set('date_to', params.date_to.trim());
    if (params?.domain_id != null) sp.set('domain_id', String(params.domain_id));
    if (params?.region_id != null) sp.set('region_id', String(params.region_id));
    const qs = sp.toString();
    return apiClient.get<ScopeTargetStats>(`/api/dashboard/scope-target-stats${qs ? `?${qs}` : ''}`);
  }

  /** Domain target summary: hierarchy domain → region → employee with target amounts (for Domains Review page). */
  async getDomainTargetSummary(year: number, month?: number | null, quarter?: number | null): Promise<DomainTargetSummaryResponse> {
    const sp = new URLSearchParams();
    sp.set('year', String(year));
    if (month != null) sp.set('month', String(month));
    if (quarter != null) sp.set('quarter', String(quarter));
    return apiClient.get<DomainTargetSummaryResponse>(`/api/dashboard/domain-target-summary?${sp.toString()}`);
  }

  /**
   * Single data source for the 4 hardcoded role dashboards. Leads/Orders/Contacts/Customers figures
   * are creator-chain scoped — identical rule to what the viewer can open on those pages.
   */
  async getRoleDashboardSummary(params?: { date_from?: string; date_to?: string }): Promise<RoleDashboardSummary> {
    const sp = new URLSearchParams();
    if (params?.date_from?.trim()) sp.set('date_from', params.date_from.trim());
    if (params?.date_to?.trim()) sp.set('date_to', params.date_to.trim());
    const qs = sp.toString();
    return apiClient.get<RoleDashboardSummary>(`/api/dashboard/role-summary${qs ? `?${qs}` : ''}`);
  }

  /** Head dashboard summary for domain_head and super_admin: region split, hot cases, conversion, won vs lost. */
  async getHeadDashboardSummary(params?: { date_from?: string; date_to?: string }): Promise<HeadDashboardSummaryResponse> {
    const sp = new URLSearchParams();
    if (params?.date_from?.trim()) sp.set('date_from', params.date_from.trim());
    if (params?.date_to?.trim()) sp.set('date_to', params.date_to.trim());
    const qs = sp.toString();
    return apiClient.get<HeadDashboardSummaryResponse>(`/api/dashboard/head-summary${qs ? `?${qs}` : ''}`);
  }

  /** Quotation submitted stats for domain_head and super_admin: region-wise counts by period. */
  async getQuotationStats(period: 'monthly' | 'quarterly' | 'till_date' = 'monthly'): Promise<QuotationStatsResponse> {
    return apiClient.get<QuotationStatsResponse>(`/api/dashboard/quotation-stats?period=${period}`);
  }

  /** Performer of the month: top employees by target achievement across all domains/regions. */
  async getPerformerOfMonth(): Promise<PerformerOfMonthResponse> {
    return apiClient.get<PerformerOfMonthResponse>('/api/dashboard/performer-of-month');
  }

  /** Lead totals per region + domain for the world map (super admin / Export domain head only; 403 for everyone else). */
  async getWorldMap(): Promise<WorldMapResponse> {
    return apiClient.get<WorldMapResponse>('/api/dashboard/world-map');
  }

  /** Star performer of the month: one company-wide winner by won value (domain heads excluded). */
  async getWonByPerson(dateFrom: string, dateTo: string): Promise<WonByPersonResponse> {
    return apiClient.get<WonByPersonResponse>(`/api/dashboard/won-by-person?date_from=${dateFrom}&date_to=${dateTo}`);
  }

  async getStarPerformer(): Promise<StarPerformerResponse> {
    return apiClient.get<StarPerformerResponse>('/api/dashboard/star-performer');
  }

  // Saved dashboards (user-created; assignable; widgets with SQL or preset)
  async getSavedDashboards(): Promise<SavedDashboardResponse[]> {
    return apiClient.get<SavedDashboardResponse[]>('/api/saved-dashboards');
  }
  async getSavedDashboard(id: number, params?: { date_from?: string; date_to?: string }): Promise<SavedDashboardResponse> {
    const sp = new URLSearchParams();
    if (params?.date_from?.trim()) sp.set('date_from', params.date_from.trim());
    if (params?.date_to?.trim()) sp.set('date_to', params.date_to.trim());
    const qs = sp.toString();
    return apiClient.get<SavedDashboardResponse>(`/api/saved-dashboards/${id}${qs ? `?${qs}` : ''}`);
  }
  async createSavedDashboard(data: { name: string; description?: string; config?: { layout?: unknown[] } }): Promise<SavedDashboardResponse> {
    return apiClient.post<SavedDashboardResponse>('/api/saved-dashboards', data);
  }
  async updateSavedDashboard(id: number, data: { name?: string; description?: string; config?: { layout?: unknown[] } }): Promise<SavedDashboardResponse> {
    return apiClient.patch<SavedDashboardResponse>(`/api/saved-dashboards/${id}`, data);
  }
  async deleteSavedDashboard(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/saved-dashboards/${id}`);
  }
  async getAssignableUsers(): Promise<AssignableUser[]> {
    return apiClient.get<AssignableUser[]>('/api/saved-dashboards/assignable-users');
  }
  async getSavedDashboardAssignments(dashboardId: number): Promise<SavedDashboardAssignmentResponse[]> {
    return apiClient.get<SavedDashboardAssignmentResponse[]>(`/api/saved-dashboards/${dashboardId}/assignments`);
  }
  async assignSavedDashboard(dashboardId: number, data: { assignee_employee_id?: number | null; role?: string | null; can_edit?: boolean }): Promise<SavedDashboardAssignmentResponse> {
    return apiClient.post<SavedDashboardAssignmentResponse>(`/api/saved-dashboards/${dashboardId}/assignments`, data);
  }
  async deleteSavedDashboardAssignment(dashboardId: number, assignmentId: number): Promise<void> {
    return apiClient.delete<void>(`/api/saved-dashboards/${dashboardId}/assignments/${assignmentId}`);
  }

  // Report templates (like dashboards: template + SQL sections, assign to employees)
  async getReportTemplates(): Promise<ReportTemplateResponse[]> {
    return apiClient.get<ReportTemplateResponse[]>('/api/report-templates');
  }
  async getReportTemplate(id: number, params?: ReportTemplateEntityParams): Promise<ReportTemplateResponse> {
    const sp = new URLSearchParams();
    if (params?.date_from?.trim()) sp.set('date_from', params.date_from.trim());
    if (params?.date_to?.trim()) sp.set('date_to', params.date_to.trim());
    if (params?.lead_id != null) sp.set('lead_id', String(params.lead_id));
    if (params?.lead_ids?.trim()) sp.set('lead_ids', params.lead_ids.trim());
    if (params?.domain_id != null) sp.set('domain_id', String(params.domain_id));
    if (params?.domain_ids?.trim()) sp.set('domain_ids', params.domain_ids.trim());
    if (params?.region_id != null) sp.set('region_id', String(params.region_id));
    if (params?.region_ids?.trim()) sp.set('region_ids', params.region_ids.trim());
    if (params?.employee_id != null) sp.set('employee_id', String(params.employee_id));
    if (params?.contact_id != null) sp.set('contact_id', String(params.contact_id));
    if (params?.contact_ids?.trim()) sp.set('contact_ids', params.contact_ids.trim());
    if (params?.customer_id != null) sp.set('customer_id', String(params.customer_id));
    if (params?.customer_ids?.trim()) sp.set('customer_ids', params.customer_ids.trim());
    if (params?.organization_id != null) sp.set('organization_id', String(params.organization_id));
    if (params?.organization_ids?.trim()) sp.set('organization_ids', params.organization_ids.trim());
    if (params?.plant_id != null) sp.set('plant_id', String(params.plant_id));
    if (params?.plant_ids?.trim()) sp.set('plant_ids', params.plant_ids.trim());
    const qs = sp.toString();
    return apiClient.get<ReportTemplateResponse>(`/api/report-templates/${id}${qs ? `?${qs}` : ''}`);
  }
  async createReportTemplate(data: { name: string; description?: string; config?: { sections?: ReportSection[] } }): Promise<ReportTemplateResponse> {
    return apiClient.post<ReportTemplateResponse>('/api/report-templates', data);
  }
  async updateReportTemplate(id: number, data: { name?: string; description?: string; config?: { sections?: ReportSection[] } }): Promise<ReportTemplateResponse> {
    return apiClient.patch<ReportTemplateResponse>(`/api/report-templates/${id}`, data);
  }
  async deleteReportTemplate(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/report-templates/${id}`);
  }
  async getReportTemplateAssignableUsers(): Promise<AssignableUser[]> {
    return apiClient.get<AssignableUser[]>('/api/report-templates/assignable-users');
  }
  async getReportTemplateAssignments(templateId: number): Promise<ReportTemplateAssignmentResponse[]> {
    return apiClient.get<ReportTemplateAssignmentResponse[]>(`/api/report-templates/${templateId}/assignments`);
  }
  async assignReportTemplate(templateId: number, data: { assignee_employee_id: number; can_edit?: boolean }): Promise<ReportTemplateAssignmentResponse> {
    return apiClient.post<ReportTemplateAssignmentResponse>(`/api/report-templates/${templateId}/assignments`, data);
  }
  async deleteReportTemplateAssignment(templateId: number, assignmentId: number): Promise<void> {
    return apiClient.delete<void>(`/api/report-templates/${templateId}/assignments/${assignmentId}`);
  }

  // async generateWidgetWithAI(data: {
  //   prompt: string;
  //   schema: { name: string; columns: { name: string; type: string }[] }[];
  //   scope_mode?: 'auto' | 'employee' | 'region' | 'domain';
  //   preferred_chart?: 'table' | 'bar' | 'line' | 'pie' | 'heatmap' | 'number-card';
  //   date_from?: string;
  //   date_to?: string;
  // }): Promise<{ title: string; chart_type: 'table' | 'bar' | 'line' | 'pie' | 'heatmap' | 'number-card'; sql: string }> {
  //   return apiClient.post<{ title: string; chart_type: 'table' | 'bar' | 'line' | 'pie' | 'heatmap' | 'number-card'; sql: string }>(
  //     '/api/saved-dashboards/ai-generate-widget',
  //     data
  //   );
  // }
  async previewSqlTemplate(data: {
    sql: string;
    chart_type?: 'table' | 'bar' | 'line' | 'pie' | 'heatmap' | 'number-card';
    schema: { name: string; columns: { name: string; type: string }[] }[];
    date_from?: string;
    date_to?: string;
  }): Promise<{ chart_type: 'table' | 'bar' | 'line' | 'pie' | 'heatmap' | 'number-card'; compiled_sql: string; data: unknown[] }> {
    return apiClient.post<{ chart_type: 'table' | 'bar' | 'line' | 'pie' | 'heatmap' | 'number-card'; compiled_sql: string; data: unknown[] }>(
      '/api/saved-dashboards/preview-sql-template',
      data
    );
  }
  /** Execute widget data source (SQL or preset); returns { data, chart_type } for charts. */
  async executeWidget(body: { chart_type: string; data_source: { kind: string; value?: string }; title?: string }): Promise<{ data: unknown[]; chart_type?: string }> {
    return apiClient.post<{ data: unknown[]; chart_type?: string }>('/api/saved-dashboards/execute-widget', body);
  }

  /** Database schema (tables and columns) for Custom SQL widgets and ER reference. */
  async getSchema(): Promise<SchemaResponse> {
    return apiClient.get<SchemaResponse>('/api/schema');
  }

  /** Set monthly target for an employee (admin/head in scope). Default 8 lacs if not set. */
  async setEmployeeTarget(employee_id: number, year: number, month: number, target_amount: number): Promise<{ ok: boolean }> {
    return apiClient.put<{ ok: boolean }>(
      `/api/dashboard/target?employee_id=${employee_id}&year=${year}&month=${month}`,
      { target_amount }
    );
  }

  /** Optional monthly goal for a region (0 clears explicit goal). */
  async setRegionTarget(region_id: number, year: number, month: number, target_amount: number): Promise<{ ok: boolean }> {
    return apiClient.put<{ ok: boolean }>(
      `/api/dashboard/target/region?region_id=${region_id}&year=${year}&month=${month}`,
      { target_amount }
    );
  }

  /** Optional monthly goal for a domain (0 clears explicit goal). */
  async setDomainTarget(domain_id: number, year: number, month: number, target_amount: number): Promise<{ ok: boolean }> {
    return apiClient.put<{ ok: boolean }>(
      `/api/dashboard/target/domain?domain_id=${domain_id}&year=${year}&month=${month}`,
      { target_amount }
    );
  }

  // Today's tasks (auto from follow-up / status group rule, or manual)
  async getTodayTasks(): Promise<TaskItem[]> {
    return apiClient.get<TaskItem[]>('/api/tasks/today');
  }
  async getTask(taskId: number): Promise<TaskItem> {
    return apiClient.get<TaskItem>(`/api/tasks/${taskId}`);
  }
  async completeTask(taskId: number): Promise<{ id: number; completed_at: string }> {
    return apiClient.patch<{ id: number; completed_at: string }>(`/api/tasks/${taskId}/complete`);
  }
  async createManualTask(data: { title: string; description?: string }): Promise<TaskItem> {
    return apiClient.post<TaskItem>('/api/tasks', data);
  }

  // Notifications
  async getNotifications(unreadOnly = false, limit = 50): Promise<NotificationItem[]> {
    const qs = new URLSearchParams();
    if (unreadOnly) qs.set('unread_only', 'true');
    if (limit !== 50) qs.set('limit', String(limit));
    const s = qs.toString();
    return apiClient.get<NotificationItem[]>(s ? `/api/notifications/?${s}` : '/api/notifications/');
  }
  async getUnreadNotificationCount(): Promise<{ count: number }> {
    return apiClient.get<{ count: number }>('/api/notifications/unread-count');
  }
  async markNotificationRead(notificationId: number): Promise<NotificationItem> {
    return apiClient.patch<NotificationItem>(`/api/notifications/${notificationId}/read`);
  }
  async markAllNotificationsRead(): Promise<{ ok: boolean }> {
    return apiClient.patch<{ ok: boolean }>('/api/notifications/read-all');
  }
  async getNotificationPreferences(): Promise<NotificationPreferences> {
    return apiClient.get<NotificationPreferences>('/api/notifications/preferences');
  }
  async updateNotificationPreferences(data: { times_per_day?: number; preferred_times?: string }): Promise<NotificationPreferences> {
    return apiClient.put<NotificationPreferences>('/api/notifications/preferences', data);
  }
  async registerNotificationDevice(data: { token: string; platform?: 'web' | 'android' | 'ios'; user_agent?: string }): Promise<{ id: number; user_employee_id: number; platform: string; created_at: string; last_seen_at?: string | null }> {
    return apiClient.post('/api/notifications/devices/register', data);
  }
  async unregisterNotificationDevice(data: { token: string; platform?: 'web' | 'android' | 'ios' }): Promise<{ ok: boolean }> {
    return apiClient.post('/api/notifications/devices/unregister', data);
  }
  async scheduleLeadFollowUp(leadId: number, data: { next_follow_up_at?: string | null; follow_up_reminder_type?: string | null; follow_up_time?: string | null }): Promise<Lead> {
    return apiClient.patch<Lead>(`/api/leads/${leadId}/follow-up`, data);
  }

  // Audit Logs
  async getAuditLogs(params?: {
    page?: number;
    page_size?: number;
    entity_type?: string;
    employee_id?: number;
    action?: string;
    /** ISO datetimes; end is inclusive. */
    start_date?: string;
    end_date?: string;
    search?: string;
  }): Promise<PaginatedResponse<AuditLog>> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(params?.page ?? 1));
    queryParams.append('page_size', String(params?.page_size ?? 25));
    if (params?.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params?.employee_id != null) queryParams.append('employee_id', String(params.employee_id));
    if (params?.action) queryParams.append('action', params.action);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.search) queryParams.append('search', params.search);
    return apiClient.get<PaginatedResponse<AuditLog>>(`/api/audit-logs/?${queryParams.toString()}`);
  }

  /** Navbar global search — leads, orders, contacts, customers, organizations the user can see. */
  /** Leads by Region (dashboard widget with include_leads=false; Reports → Leads by Region page). */
  async getLeadsByRegion(params: LeadsByRegionParams = {}): Promise<LeadsByRegionResponse> {
    const qp = new URLSearchParams();
    if (params.date_from) qp.append('date_from', params.date_from);
    if (params.date_to) qp.append('date_to', params.date_to);
    if (params.domain_id != null) qp.append('domain_id', String(params.domain_id));
    if (params.region_id != null) qp.append('region_id', String(params.region_id));
    if (params.status_id != null) qp.append('status_id', String(params.status_id));
    (params.assigned_to || []).forEach(id => qp.append('assigned_to', String(id)));
    if (params.search?.trim()) qp.append('search', params.search.trim());
    if (params.include_leads === false) qp.append('include_leads', 'false');
    return apiClient.get<LeadsByRegionResponse>(`/api/leads-by-region/?${qp.toString()}`);
  }

  async globalSearch(q: string): Promise<GlobalSearchResponse> {
    return apiClient.get<GlobalSearchResponse>(`/api/search/?${new URLSearchParams({ q })}`);
  }

  async getAuditLogFilters(): Promise<AuditLogFilterOptions> {
    return apiClient.get<AuditLogFilterOptions>('/api/audit-logs/filters');
  }

  // User presence (in-memory, nothing persisted)
  async pingPresence(page: string, label?: string, idleSeconds = 0): Promise<void> {
    return apiClient.post('/api/presence/ping', { page, label, idle_seconds: idleSeconds });
  }

  async getActiveUsers(): Promise<PresenceActiveResponse> {
    return apiClient.get<PresenceActiveResponse>('/api/presence/active');
  }

  // Visibility Settings
  async getMarketingSettings(): Promise<MarketingSettingsPayload> {
    return apiClient.get<MarketingSettingsPayload>('/api/marketing/settings');
  }

  async updateMarketingSettings(payload: MarketingSettingsPayload): Promise<MarketingSettingsPayload> {
    return apiClient.put<MarketingSettingsPayload>('/api/marketing/settings', payload);
  }

  // What's New / Versions CRUD
  async getWhatsNew(): Promise<ChangelogVersionResponse[]> {
    return apiClient.get<ChangelogVersionResponse[]>('/api/whats-new/');
  }

  async createWhatsNew(data: Omit<ChangelogVersionResponse, 'id' | 'created_at'>): Promise<ChangelogVersionResponse> {
    return apiClient.post<ChangelogVersionResponse>('/api/whats-new/', data);
  }

  async updateWhatsNew(id: number, data: Omit<ChangelogVersionResponse, 'id' | 'created_at'>): Promise<ChangelogVersionResponse> {
    return apiClient.put<ChangelogVersionResponse>(`/api/whats-new/${id}`, data);
  }

  async deleteWhatsNew(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/whats-new/${id}`);
  }

  // ─── Exhibition / Roadshow Events ────────────────────────────

  async getEvents(params?: {
    page?: number;
    page_size?: number;
    type?: EventType;
    status?: EventStatus;
    search?: string;
  }): Promise<PaginatedResponse<ExhibitionEvent>> {
    const q = new URLSearchParams();
    q.append('page', String(params?.page ?? 1));
    q.append('page_size', String(params?.page_size ?? DEFAULT_PAGE_SIZE));
    if (params?.type) q.append('type', params.type);
    if (params?.status) q.append('status', params.status);
    if (params?.search) q.append('search', params.search);
    return apiClient.get<PaginatedResponse<ExhibitionEvent>>(`/api/events/?${q.toString()}`);
  }

  async getEvent(id: number): Promise<ExhibitionEvent> {
    return apiClient.get<ExhibitionEvent>(`/api/events/${id}`);
  }

  /** Currently-active exhibitions, trimmed shape, scoped to the caller's domain(s). For pickers. */
  async getActiveExhibitions(): Promise<ExhibitionLite[]> {
    return apiClient.get<ExhibitionLite[]>('/api/exhibitions/active');
  }

  /** Leads attributed to this exhibition/roadshow (Lead.exhibition_id === id), with value roll-ups for the ROI view. */
  async getEventLeadAttribution(id: number): Promise<EventLeadAttribution> {
    return apiClient.get<EventLeadAttribution>(`/api/events/${id}/lead-attribution`);
  }

  async createEvent(data: EventCreateInput): Promise<ExhibitionEvent> {
    return apiClient.post<ExhibitionEvent>('/api/events/', data);
  }

  async updateEvent(id: number, data: EventUpdateInput): Promise<ExhibitionEvent> {
    return apiClient.put<ExhibitionEvent>(`/api/events/${id}`, data);
  }

  async deleteEvent(id: number): Promise<void> {
    return apiClient.delete<void>(`/api/events/${id}`);
  }

  async endEvent(id: number): Promise<ExhibitionEvent> {
    return apiClient.post<ExhibitionEvent>(`/api/events/${id}/end`);
  }

  async uploadEventFile(
    eventId: number,
    fileType: 'stall_design' | 'banner_design' | 'travel_ticket' | 'local_travel_proof',
    file: File,
    vendorId?: number,
    onProgress?: (percent: number) => void,
    entryIndex?: number,
  ): Promise<UploadedFileResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    if (vendorId != null) formData.append('vendor_id', String(vendorId));
    if (entryIndex != null) formData.append('entry_index', String(entryIndex));
    return apiClient.postFormDataWithProgress<UploadedFileResponse>(
      `/api/events/${eventId}/files`,
      formData,
      onProgress,
    );
  }

  async getEventFileDownloadUrl(eventId: number, fileId: number): Promise<string> {
    const blob = await apiClient.getBlob(`/api/events/${eventId}/files/${fileId}/download`);
    return URL.createObjectURL(blob);
  }

  async downloadEventFile(eventId: number, fileId: number, fileName: string): Promise<void> {
    const blob = await apiClient.getBlob(`/api/events/${eventId}/files/${fileId}/download`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'file';
    a.click();
    URL.revokeObjectURL(url);
  }

  async deleteEventFile(eventId: number, fileId: number): Promise<void> {
    return apiClient.delete<void>(`/api/events/${eventId}/files/${fileId}`);
  }

  // Support Tickets (PQ Platform)
  async getTickets(limit: number = 20): Promise<{ success: boolean; tickets: SupportTicket[] }> {
    return apiClient.get<{ success: boolean; tickets: SupportTicket[] }>(`/api/tickets/?limit=${limit}`);
  }

  async createTicket(data: { title: string; description?: string; ticket_type: string }): Promise<{ success: boolean; crm_synced: boolean; crm_sync_error: string | null; ticket: SupportTicket }> {
    return apiClient.post<{ success: boolean; crm_synced: boolean; crm_sync_error: string | null; ticket: SupportTicket }>('/api/tickets/', data);
  }

  async sendFeedback(data: { rating: number; comment?: string }): Promise<{ success: boolean; crm_synced: boolean; crm_sync_error: string | null; ticket: SupportTicket }> {
    return apiClient.post<{ success: boolean; crm_synced: boolean; crm_sync_error: string | null; ticket: SupportTicket }>('/api/tickets/feedback', data);
  }
}

export interface SupportTicket {
  id: number;
  ticket_id: string | null;
  title: string;
  description?: string;
  ticket_type: string;
  rating?: number;
  created_at: string;
}

export interface ChangelogSection {
  title: string;
  items: string[];
}

export interface ChangelogVersionResponse {
  id: number;
  version: string;
  release_date: string;
  sections: ChangelogSection[];
  created_at: string;
}

export interface QuotationListItem {
  id: number;
  quotation_number: string | null;
  file_name: string;
  activity_id: number;
  inquiry_number: number | null;
  lead_id: number;
  lead_series: string | null;
  lead_name: string;
  activity_title: string;
  activity_date: string | null;
  media_exists?: boolean;
}

export interface QuotationFilterOptions {
  industries: string[];
  quote_series_codes: string[];
}

// Reports types
export interface ReportableEmployee {
  id: number;
  name: string;
  username?: string;
  domain_id?: number;
  domain_name?: string;
  region_id?: number;
  region_name?: string;
}
/** GET /api/leads-by-region/ — see au-marketing-api/app/region_report.py for what counts in a period. */
export interface LeadsByRegionParams {
  date_from?: string;
  date_to?: string;
  domain_id?: number;
  region_id?: number;
  status_id?: number;
  assigned_to?: number[];
  search?: string;
  include_leads?: boolean;
}
export interface RegionLeadRow {
  lead_id: number;
  name: string;
  company?: string | null;
  series?: string | null;
  status_label?: string | null;
  owner_name?: string | null;
  created_at?: string | null;
  quotation_count: number;
  quotation_value: number;
  won: boolean;
  won_value: number;
  won_at?: string | null;
}
export interface RegionTotals {
  lead_count: number;
  lead_value?: number; // potential value of the leads counted
  lost_count?: number; // leads marked lost in the period
  lost_value?: number; // potential value of those lost leads
  quotation_count: number;
  quotation_value: number;
  won_count: number;
  won_value: number;
}
export interface RegionGroup extends RegionTotals {
  region_id: number | null;
  region_name: string;
  domain_id?: number | null;
  domain_name?: string | null;
  leads: RegionLeadRow[];
}
export interface LeadsByRegionResponse {
  date_from?: string | null;
  date_to?: string | null;
  totals: RegionTotals;
  regions: RegionGroup[];
}

export interface ReportScopeResponse {
  can_select_employee: boolean;
  employees: ReportableEmployee[];
  role: 'self' | 'region_head' | 'domain_head' | 'super_admin';
  is_domain_coordinator?: boolean;
  domains?: { id: number; name: string }[];
  regions?: { id: number; name: string }[];
}
export interface InquiriesByTypeItem {
  activity_type: string;
  count: number;
}
export interface LeadsByStatusItem {
  status_id: number;
  status_code: string;
  status_label: string;
  count: number;
}
export interface ReportSummaryResponse {
  date_from?: string | null;
  date_to?: string | null;
  employee_id?: number | null;
  employee_name?: string | null;
  inquiries_count: number;
  inquiries_by_type: InquiriesByTypeItem[];
  quotations_sent_count: number;
  leads_total: number;
  leads_by_status: LeadsByStatusItem[];
  leads_created_count: number;
  total_contacts: number;
  total_customers: number;
}

export interface ExpectedOrderReportLeadItem {
  lead_id: number;
  lead_series: string | null;
  lead_name: string | null;
  company: string | null;
  lead_status_label: string | null;
  lead_is_final: boolean;
  lead_is_lost: boolean;
}
export interface ExpectedOrderReportItem {
  id: number;
  employee_id: number;
  year: number;
  month: number;
  created_at: string;
  leads: ExpectedOrderReportLeadItem[];
}

export interface ODPlanEntryItem {
  id: number;
  plan_date: string;
  entry_type: string;
  where_place: string | null;
  travel_time: string | null;
  travel_type: string | null;
  contact_id: number | null;
  contact_name: string | null;
  contact_email: string | null;
  organization_id: number | null;
  organization_name: string | null;
  plant_id: number | null;
  plant_name: string | null;
  is_unplanned: boolean;
  notes: string | null;
}
export interface ODPlanReportItem {
  id: number;
  employee_id: number;
  year: number;
  month: number;
  created_at: string;
  updated_at: string;
  entries: ODPlanEntryItem[];
}
export interface MISSummary {
  leads_created: number; activities: number; quotations_sent: number; quotations_value: number;
  won_count: number; won_value: number; won_from_plan: number; won_outside_plan: number; lost_count: number;
  orders_created: number; orders_value: number; contacts_added: number; customers_added: number;
  od_days_planned: number; od_planned_visits: number; od_unplanned_visits: number; expected_order_leads: number;
}
export interface MISLeadItem {
  lead_id: number; lead_series: string | null; lead_name: string | null; company: string | null;
  value: number | null; at: string | null; planned: boolean | null;
}
export interface MISEmployeeResponse {
  employee_id: number; employee_name: string; year: number; month: number; summary: MISSummary;
  won_leads: MISLeadItem[]; lost_leads: MISLeadItem[];
}
export type MISPlanStatus = 'on_time' | 'late' | 'not_filed' | 'pending';
export interface MISTeamRow {
  employee_id: number; employee_name: string; region_name: string | null; leads_created: number; activities: number;
  won_count: number; won_value: number; lost_count: number; orders_created: number;
  od_plan_filled: boolean; od_planned_visits: number; od_unplanned_visits: number; expected_order_filled: boolean;
  eo_total: number; eo_won: number; eo_lost: number; eo_open: number;
  next_od_status: MISPlanStatus; next_eo_status: MISPlanStatus;
  target: number; last_active: string | null;
  prev_leads_created: number; prev_activities: number; prev_won_count: number; prev_won_value: number; prev_lost_count: number;
}
export interface MISTeamResponse { year: number; month: number; rows: MISTeamRow[] }

export interface ODPlanEntryCreate {
  plan_date: string;
  entry_type: string;
  where_place?: string | null;
  travel_time?: string | null;
  travel_type?: string | null;
  contact_id?: number | null;
  organization_id?: number | null;
  plant_id?: number | null;
  is_unplanned?: boolean;
  notes?: string | null;
}

export interface DashboardTargetStats {
  monthly_target: number;
  achieved_this_month: number;
  won_leads_count_this_month: number;
  lost_leads_count_this_month: number;
  year: number;
  month: number;
}

/** Scope-wide target stats for dashboard (employee = own, heads = aggregated team). */
export interface ScopeTargetStats {
  role: 'self' | 'region_head' | 'domain_head' | 'super_admin';
  scope_label: string;
  monthly_target: number;
  achieved_this_month: number;
  won_leads_count_this_month: number;
  lost_leads_count_this_month: number;
  quotation_submitted_value: number;
  year: number;
  month: number;
  employee_count: number;
}

/** One of the 4 hardcoded role dashboards (Employee / Region Head / Domain Head / Super Admin). */
export type DashboardRole = 'employee' | 'region_head' | 'domain_head' | 'super_admin';

export interface DashboardStatusCount {
  status_id?: number | null;
  status: string;
  count: number;
  color?: string | null;
  is_final: boolean;
  is_lost: boolean;
}

export interface DashboardRecentLead {
  id: number;
  series?: string | null;
  company?: string | null;
  status?: string | null;
  status_color?: string | null;
  potential_value?: number | null;
  created_at: string;
}

export interface DashboardMonthPoint {
  month: string;
  label: string;
  lead_count: number;
  won_value: number;
  order_revenue: number;
  target_value?: number;
}

export interface DashboardRevenuePipeline {
  achieved: number;
  committed: number;
  pipeline: number;
  pipeline_quoted?: number; // quotation value on the same open leads
}

export interface DashboardFollowUp {
  id: number;
  series?: string | null;
  company?: string | null;
  next_follow_up_at: string;
  due_label: string;
  /** 'auto' = due by the status group's "every N days" rule; 'manual' = a date set on the lead. */
  source?: 'manual' | 'auto';
}

export interface DashboardLeadSource {
  source: string;
  count: number;
}

export interface DashboardHighValueLead {
  id: number;
  series?: string | null;
  company?: string | null;
  value: number;
  value_source: 'potential' | 'quote';
  status?: string | null;
}

export interface RoleDashboardSummary {
  dashboard_role: DashboardRole;
  scope_label: string;
  year: number;
  month: number;
  total_leads: number;
  open_leads: number;
  won_count_month: number;
  lost_count_month: number;
  conversion_ratio_pct: number | null;
  by_status: DashboardStatusCount[];
  recent_leads: DashboardRecentLead[];
  monthly_trend: DashboardMonthPoint[];
  total_orders: number;
  total_revenue_month: number;
  contacts_count: number;
  customers_count: number;
  monthly_target: number;
  achieved_this_month: number;
  employee_count: number;
  avg_open_lead_age_days: number | null;
  revenue_pipeline: DashboardRevenuePipeline;
  hot_leads_count: number;
  follow_ups_due: DashboardFollowUp[];
  follow_ups_total?: number; // all that need follow-up (the list holds the first 8)
  won_by_day?: { day: number; value: number }[]; // running total of won value per day this month (pace chart)
  lead_source_breakdown: DashboardLeadSource[];
  high_value_leads: DashboardHighValueLead[];
}

/** Domain target summary: hierarchy domain → region → employee with target amounts (for Domains Review page). */
export interface EmployeeTargetItem {
  employee_id: number;
  employee_name: string;
  target_amount: number;
}

export interface RegionTargetSummaryItem {
  region_id: number;
  region_name: string;
  region_code?: string | null;
  total_target: number;
  /** Explicit region goal; team sum is still `total_target`. */
  assigned_target?: number | null;
  employees: EmployeeTargetItem[];
}

export interface DomainTargetSummaryItem {
  domain_id: number;
  domain_name: string;
  domain_code?: string | null;
  total_target: number;
  /** Explicit domain goal; team sum is still `total_target`. */
  assigned_target?: number | null;
  regions: RegionTargetSummaryItem[];
}

export interface DomainTargetSummaryResponse {
  year: number;
  month?: number | null;
  total_target: number;
  domains: DomainTargetSummaryItem[];
}

/** Region row for head dashboard (domain_head / super_admin). */
export interface RegionBreakdownItem {
  region_id: number;
  region_name: string;
  domain_name: string;
  total_leads: number;
  won_count: number;
  lost_count: number;
  hot_cases_count: number;
  conversion_ratio_pct: number | null;
}

/** Head dashboard summary: region-wise split, hot cases, total leads, conversion, won vs lost. */
export interface HeadDashboardSummaryResponse {
  role: string;
  region_breakdown: RegionBreakdownItem[];
  hot_cases_count: number;
  total_leads: number;
  conversion_ratio_pct: number | null;
  won_count: number;
  lost_count: number;
  year: number;
  month: number;
}

export interface WorldMapRow {
  domain_id: number; domain_name: string; is_export: boolean; region_id: number; region_name: string;
  lead_count: number; open_count: number; won_count: number; won_value: number; pipeline_value: number;
}
export interface WorldMapResponse { rows: WorldMapRow[]; no_region_count: number }

export interface StarPerformer {
  employee_id: number;
  employee_name: string;
  domain_name: string | null;
  region_name: string | null;
  profile_picture: string | null;
}

export interface StarPerformerResponse {
  year: number;
  month: number;
  performer: StarPerformer | null;
}

export interface WonByPersonItem {
  employee_id: number;
  employee_name: string;
  won_value: number;
  won_count: number;
}

export interface WonByPersonResponse {
  date_from: string;
  date_to: string;
  total_value: number;
  total_count: number;
  people: WonByPersonItem[];
}

export interface PerformerOfMonthItem {
  employee_id: number;
  employee_name: string;
  domain_name: string;
  region_name: string | null;
  monthly_target: number;
  achieved_value: number;
  achievement_pct: number;
  won_count: number;
}

export interface PerformerOfMonthResponse {
  year: number;
  month: number;
  performers: PerformerOfMonthItem[];
  highest_absolute_achiever: PerformerOfMonthItem | null;
}

/** A user currently active on the platform (in-memory presence tracking). */
export interface PresenceUser {
  employee_id: number;
  employee_name: string;
  page: string;
  /** Optional human-readable detail for the current page, e.g. the record's name ("Acme Corp"). */
  label?: string | null;
  last_seen_at: number;
  seconds_ago: number;
  /** How long ago they last used the screen. Missing on an older server (treated as active). */
  idle_seconds?: number;
  /** active = used it in the last 3 min, idle = 3 to 15 min, away = tab open but unattended for longer. */
  status?: 'active' | 'idle' | 'away';
  profile_picture?: string | null;
}

export interface PresenceActiveResponse {
  active_seconds: number;
  users: PresenceUser[];
}

/** Quotation stats: region-wise counts per period label (month/quarter/all-time). */
export interface QuotationStatsResponse {
  labels: string[];
  regions: { region_name: string; data: number[] }[];
}

/** User who can be assigned a dashboard (domain head, region head, or employee in marketing). */
export interface AssignableUser {
  id: number;
  name: string;
  role_hint?: string | null;
}

/** Saved dashboard (user-created; config.layout = widgets). */
export interface SavedDashboardResponse {
  id: number;
  name: string;
  description: string | null;
  config: { layout?: unknown[] } | null;
  widget_data?: Record<string, { data?: unknown[]; chart_type?: string; error?: string }>;
  domain_id: number | null;
  created_by_employee_id: number;
  created_by_username: string | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
}

/** Assignment of a saved dashboard to a user (employee). */
export interface SavedDashboardAssignmentResponse {
  id: number;
  dashboard_id: number;
  assignee_employee_id?: number | null;
  role?: string | null;
  can_edit: boolean;
  created_at: string;
}

/** One section in a report template: title + SQL. */
export interface ReportSection {
  id: string;
  title?: string;
  sql: string;
  display_order?: number;
  chart_type?: string;
}

/** Report template (user-created; config.sections = list of SQL sections). */
export interface ReportTemplateResponse {
  id: number;
  name: string;
  description: string | null;
  config: { sections?: ReportSection[] } | null;
  section_data?: Record<string, { data?: unknown[]; error?: string }>;
  domain_id: number | null;
  created_by_employee_id: number;
  created_by_username: string | null;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  /** Placeholder keys used in section SQL (e.g. lead_id, domain_id) for filter dropdowns */
  placeholders?: string[] | null;
}

/** Entity params for report template run (single ID or comma-separated _ids). */
export interface ReportTemplateEntityParams {
  date_from?: string;
  date_to?: string;
  lead_id?: number;
  lead_ids?: string;
  domain_id?: number;
  domain_ids?: string;
  region_id?: number;
  region_ids?: string;
  employee_id?: number;
  contact_id?: number;
  contact_ids?: string;
  customer_id?: number;
  customer_ids?: string;
  organization_id?: number;
  organization_ids?: string;
  plant_id?: number;
  plant_ids?: string;
}

/** Assignment of a report template to a user (employee). */
export interface ReportTemplateAssignmentResponse {
  id: number;
  template_id: number;
  assignee_employee_id: number;
  can_edit: boolean;
  created_at: string;
}

export interface SchemaColumnInfo {
  name: string;
  type: string;
  nullable: boolean;
}

export interface SchemaForeignKeyInfo {
  constrained_columns: string[];
  referred_table: string;
  referred_columns: string[];
}

export interface SchemaTableInfo {
  name: string;
  columns: SchemaColumnInfo[];
  foreign_keys: SchemaForeignKeyInfo[];
}

export interface SchemaResponse {
  tables: SchemaTableInfo[];
}

export interface TaskItem {
  id: number;
  employee_id: number;
  title: string;
  description: string | null;
  due_date: string;
  source: string;
  lead_id: number | null;
  order_id: number | null;
  lead_status_group_id: number | null;
  completed_at: string | null;
  created_at: string;
  lead_series: string | null;
  lead_name: string | null;
}

// Notifications
export interface NotificationItem {
  id: number;
  user_employee_id: number;
  title: string;
  message: string | null;
  link: string | null;
  notification_type: string | null;
  lead_id: number | null;
  read_at: string | null;
  created_at: string;
}
export interface NotificationPreferences {
  user_employee_id: number;
  times_per_day: number | null;
  preferred_times: string | null;
  updated_at: string;
}

// Numbering Series types
export type SeriesResetPeriod = 'none' | 'day' | 'week' | 'month' | 'year' | 'financial_year';

export interface Series {
  id: number;
  name: string;
  code: string;
  pattern: string;
  entity_type?: string | null;
  next_value: number;
  reset_period: SeriesResetPeriod;
  last_period_key?: string | null;
  last_generated_at?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SeriesCreateInput {
  name: string;
  code: string;
  pattern: string;
  entity_type?: string | null;
  next_value?: number;
  reset_period?: SeriesResetPeriod;
  is_active?: boolean;
}

export interface SeriesUpdateInput {
  name?: string;
  code?: string;
  pattern?: string;
  entity_type?: string | null;
  next_value?: number;
  reset_period?: SeriesResetPeriod;
  is_active?: boolean;
}

export interface SeriesGenerateResponse {
  series_id: number;
  series_code: string;
  generated_value: string;
}

// Visibility Settings Interfaces
export interface EmployeeRules {
  view_own_target: boolean;
  view_other_employee_targets: boolean;
  view_region_head_name: boolean;
  view_domain_head_name: boolean;
  view_region_target: boolean;
}

export interface RegionHeadRules {
  view_other_regions: boolean;
  view_domain_head_name: boolean;
  view_domain_target: boolean;
}

export interface DomainHeadRules {
  view_other_domains: boolean;
  view_other_regions: boolean;
}

/** Domain Coordinator: assists Domain Head, elevated visibility within a domain */
export interface DomainCoordinatorRules {
  view_other_domains: boolean;
  view_other_regions: boolean;
  view_region_targets: boolean;
  view_employee_targets: boolean;
}

/** Region Coordinator: assists Region Head, elevated visibility within a region */
export interface RegionCoordinatorRules {
  view_other_regions: boolean;
  view_domain_head_name: boolean;
  view_domain_target: boolean;
  view_employee_targets: boolean;
}

export interface GlobalRules {
  domain_head: DomainHeadRules;
  domain_coordinator: DomainCoordinatorRules;
  region_head: RegionHeadRules;
  region_coordinator: RegionCoordinatorRules;
  employee: EmployeeRules;
}

export interface PastQuarterAccess {
  quarter: string;
  user_ids: number[];
  user_details?: { id: number; first_name: string; last_name: string; role?: string }[];
}

export interface MarketingSettingsPayload {
  schema_version: number;
  global_rules: GlobalRules;
  domain_overrides: Record<string, GlobalRules>;
  past_quarter_visible_user_ids?: number[];
  past_quarter_access?: PastQuarterAccess[];
}

// ─── Exhibition / Roadshow (Event) Types ─────────────────────────────────

export interface Installment {
  due_date: string;
  amount: number;
  paid: boolean;
}

export interface StallVendor {
  id: number;
  name: string;
}

export interface LocalTravelEntry {
  note: string;
  amount: number;
  employee_ids: number[];
}

export interface GiftingEntry {
  name: string;
  count: number;
  amount: number;
}

export interface DesignFile {
  id: number;
  vendor_id?: number | null;
  file_name: string;
  file_url: string;
  revision_no: number;
  is_selected: boolean;
  created_at: string;
}

export interface TravelTicket {
  id: number;
  employee_id: number;
  file_name: string;
  file_url: string;
  created_at: string;
}

export type EventType = 'exhibition' | 'roadshow';
export type EventStatus = 'active' | 'ended';
export type PaymentStatus = 'pending' | 'partial' | 'completed';
export type BannerSource = 'stall_vendor' | 'own';

export interface ExhibitionEvent {
  id: number;
  domain_id: number;
  type: EventType;
  name: string;
  location: string;
  start_date: string;
  end_date: string;
  status: EventStatus;
  budget: number;
  total_spent: number;
  selected_employee_ids: number[];
  created_by_employee_id?: number;
  created_by_username?: string;
  created_at: string;
  updated_at: string;
  // Space Booking
  space_booking_vendor: string;
  space_booking_amount: number;
  space_booking_paid_amount: number;
  space_booking_pi_sent: boolean;
  space_booking_payment_status: PaymentStatus;
  space_booking_installments: Installment[];
  // Stall Design (exhibition)
  stall_vendors: StallVendor[];
  stall_selected_vendor_id?: number | null;
  stall_po_created: boolean;
  stall_design_files: DesignFile[];
  // Banner Design (exhibition)
  banner_design_source: BannerSource;
  banner_design_files: DesignFile[];
  // Table Booking (roadshow)
  table_booking_venue: string;
  table_booking_count: number;
  table_booking_cost_per_table: number;
  table_booking_total_cost: number;
  // Travel
  travel_days_before: number;
  travel_employee_ids: number[];
  travel_cost: number;
  travel_tickets: TravelTicket[];
  // Hotel
  hotel_name: string;
  hotel_employee_ids: number[];
  hotel_cost: number;
  // Local Travel
  local_travel_entries: LocalTravelEntry[];
  local_travel_proofs: UploadedFileResponse[];
  // Gifting
  gifting_entries: GiftingEntry[];
}

/** One lead attributed to an exhibition (from GET /api/events/:id/lead-attribution). */
export interface AttributedLead {
  id: number;
  series?: string | null;
  contact_name?: string | null;
  company_name?: string | null;
  status?: string | null;
  is_won: boolean;
  is_lost: boolean;
  potential_value: number;
  closed_value: number;
}

/** Lead attribution + value roll-ups for an exhibition's ROI view. */
export interface EventLeadAttribution {
  event_id: number;
  leads_count: number;
  won_count: number;
  lost_count: number;
  open_count: number;
  pipeline_value: number;        // sum of potential_value for still-open leads
  won_value: number;             // sum of closed_value for won leads
  total_potential_value: number; // sum of potential_value across every attributed lead
  leads: AttributedLead[];
}

/** Trimmed exhibition shape from GET /api/exhibitions/active (domain-scoped picker list). */
export interface ExhibitionLite {
  id: number;
  name: string;
  location: string;
  start_date: string;
  end_date: string;
  domain_id: number;
}

export interface EventCreateInput {
  domain_id: number;
  type: EventType;
  name: string;
  location: string;
  start_date: string;
  end_date: string;
  budget: number;
  selected_employee_ids: number[];
}

export interface EventUpdateInput {
  domain_id?: number;
  name?: string;
  location?: string;
  start_date?: string;
  end_date?: string;
  budget?: number;
  selected_employee_ids?: number[];
  // Phase fields
  space_booking_vendor?: string;
  space_booking_amount?: number;
  space_booking_paid_amount?: number;
  space_booking_pi_sent?: boolean;
  space_booking_payment_status?: PaymentStatus;
  space_booking_installments?: Installment[];
  stall_vendors?: StallVendor[];
  stall_selected_vendor_id?: number | null;
  stall_po_created?: boolean;
  banner_design_source?: BannerSource;
  table_booking_venue?: string;
  table_booking_count?: number;
  table_booking_cost_per_table?: number;
  travel_days_before?: number;
  travel_employee_ids?: number[];
  travel_cost?: number;
  hotel_name?: string;
  hotel_employee_ids?: number[];
  hotel_cost?: number;
  local_travel_entries?: LocalTravelEntry[];
  gifting_entries?: GiftingEntry[];
}

export interface UploadedFileResponse {
  id: number;
  file_name: string;
  file_url: string;
  entry_index?: number;
}

export const marketingAPI = new MarketingAPIService();
